/**
 * Assistant app actions — the AI help assistant's "hands".
 *
 * A single client-side registry of safe UI actions the assistant (Voice Mode
 * and the text help chat) can perform: open a screen, open an app's settings,
 * visually highlight an element, and read where the user currently is.
 *
 * Phase 1 is deliberately navigation/visual only — nothing here writes data.
 *
 * The catalogs below are the single source of truth: the client sends them to
 * the ai-voice-session / ai-help edge functions, which build the model's tool
 * schemas from them (edge functions cannot import from src/).
 */

import { deepLinkNavigate } from '../deepLinkNavigation';
import { supabase } from '../supabase';
import { askSurface, getOpenAssistantSurfaces } from './surface';

/** Window event the highlight overlay listens for. */
export const ASSISTANT_HIGHLIGHT_EVENT = 'assistant:highlight';

export interface AssistantDestination {
  id: string;
  label: string;
  path: string;
  description: string;
  /** Display name of the app that must be installed (matches installedApps names). */
  requiresApp?: string;
}

export const ASSISTANT_DESTINATIONS: AssistantDestination[] = [
  { id: 'control_centre', label: 'Control Centre', path: '/admin-dashboard', description: 'The admin dashboard: actions required, business metrics, panels for CRM/marketing/etc.' },
  { id: 'calendar', label: 'Calendar', path: '/calendarV2', description: 'The calendar with all bookings (month/week/day views).' },
  { id: 'messages', label: 'Messages', path: '/messages', description: 'The unified inbox: SMS, email, calls, callbacks.' },
  { id: 'customers', label: 'Customers', path: '/customers', description: 'Customer records and profiles.' },
  { id: 'invoices', label: 'Invoices', path: '/invoices', description: 'Invoices, payments, and receivables.', requiresApp: 'Invoices' },
  { id: 'proposals', label: 'Proposals', path: '/proposals', description: 'Quotes/estimates the customer approves online.', requiresApp: 'Proposals' },
  { id: 'crm', label: 'CRM', path: '/crm', description: 'Sales pipelines, leads, and opportunities.', requiresApp: 'CRM' },
  { id: 'lead_forms', label: 'Lead Forms', path: '/crm/lead-forms', description: 'The lead forms library (build and share lead capture forms).', requiresApp: 'CRM' },
  { id: 'automations', label: 'Automations', path: '/automations', description: 'The Automate app: automations AND AI agents (receptionist, feedback agent) live here.', requiresApp: 'Automate' },
  { id: 'marketing_overview', label: 'Marketing — Overview', path: '/marketing', description: 'Marketing home: connected channels, media calendar, Media Manager.', requiresApp: 'Marketing' },
  { id: 'marketing_campaigns', label: 'Marketing — Campaigns', path: '/marketing?tab=campaigns', description: 'Email/SMS campaign list and creation.', requiresApp: 'Marketing' },
  { id: 'marketing_email_templates', label: 'Marketing — Email Templates', path: '/marketing?tab=templates', description: 'Saved email templates for reuse, and one-off emails written for one person.', requiresApp: 'Marketing' },
  { id: 'marketing_discount_codes', label: 'Marketing — Discount Codes', path: '/marketing?tab=discounts', description: 'Create and manage discount codes.', requiresApp: 'Marketing' },
  { id: 'marketing_referral_program', label: 'Marketing — Referral Program', path: '/marketing?tab=referral', description: 'Referral program settings and rewards.', requiresApp: 'Marketing' },
  { id: 'marketing_rewards_program', label: 'Marketing — Rewards Program', path: '/marketing?tab=rewards', description: 'Points program: spend tiers and rewards customers can redeem.', requiresApp: 'Marketing' },
  { id: 'marketing_brand_guidelines', label: 'Marketing — Brand Guidelines', path: '/marketing?tab=brand', description: 'The brand kit AI writers use: logo, colors, voice, language rules.', requiresApp: 'Marketing' },
  { id: 'media_manager', label: 'Media Manager', path: '/marketing?view=media', description: 'Media library + social posting workspaces (below the calendar on Marketing Overview).', requiresApp: 'Marketing' },
  { id: 'google_business', label: 'Google Business workspace', path: '/marketing?view=google-business', description: 'Google Business Profile reviews, posts, and photos.', requiresApp: 'Marketing' },
  { id: 'teams', label: 'Teams', path: '/resources', description: 'Team members, roles, working hours.', requiresApp: 'Teams' },
  { id: 'payroll', label: 'T-Sheets (Payroll)', path: '/payroll', description: 'Payroll runs, timesheets, paystubs.', requiresApp: 'T-Sheets' },
  { id: 'expenses', label: 'Expenses', path: '/expenses', description: 'Expense tracking and receipt uploads.', requiresApp: 'Expenses' },
  { id: 'docs', label: 'Docs', path: '/documents', description: 'Document templates, checklists, signed forms.', requiresApp: 'Docs' },
  { id: 'projects', label: 'Projects', path: '/projects', description: 'Multi-visit projects and field quotes.' },
  { id: 'services', label: 'Services', path: '/services', description: 'The service catalog with price options and add-ons.' },
  { id: 'inventory', label: 'Inventory', path: '/stock', description: 'Stock, products, and materials.', requiresApp: 'Inventory' },
  { id: 'gift_cards', label: 'Gift Cards', path: '/gift-cards', description: 'Sell and manage gift cards.', requiresApp: 'Gift Cards' },
  { id: 'analytics', label: 'Analytics', path: '/reports', description: 'Reports and business insights.', requiresApp: 'Analytics' },
  { id: 'nexus_connect', label: 'Nexus Connect', path: '/app-store', description: 'Install apps, buy add-ons (Nexus Apps tab).' },
  { id: 'integrations', label: 'Nexus Connect — Integrations', path: '/app-store?tab=integrations', description: 'Connect Gmail, Twilio, Stripe, Google Calendar, Google Business Profile, Facebook, Instagram.' },
  { id: 'my_subscription', label: 'My Apps & Subscription', path: '/my-apps', description: 'Plan, billing, add-ons, website hosting.' },
  { id: 'business_settings', label: 'Business Settings', path: '/business-settings', description: 'Business name, address, logo, GST/PST numbers, tax rates.' },
  { id: 'ai_settings', label: 'AI Settings', path: '/ai-settings', description: 'AI token balance, buying/topping up AI tokens, auto top-up, AI usage history. Anything about "AI settings" or tokens goes HERE (a screen, not an app-settings modal).' },
  { id: 'online_booking_builder', label: 'Online Booking builder', path: '/mobile-calendar/online-booking', description: 'Configure the customer-facing online booking page.' },
];

/** App-settings modal slugs the assistant may open (dispatches openAppSettings). */
export const ASSISTANT_APP_SETTINGS: Array<{ slug: string; label: string; rows?: string[] }> = [
  {
    slug: 'calendar',
    label: 'Calendar settings',
    rows: ['Default Calendar View', 'Enable Map View', 'Sales Pins', 'Location Booking', 'Mobile Booking', 'Field Quotes', 'Create Field Quotes from bookings', 'Events', 'Service Areas', 'Bookings by Service Area', 'Buffer Time', 'Online Booking', 'Resource Working on Job', 'Current Bookings Helper', 'Feedback Collection', 'Liability Waiver'],
  },
  {
    slug: 'mobile-calendar',
    label: 'Field-Pro settings (service areas, mobile booking)',
    rows: ['Mobile Booking', 'Service Areas', 'Bookings by Service Area', 'Online Booking', 'Feedback Collection', 'Liability Waiver'],
  },
  { slug: 'customers', label: 'Customers settings' },
  { slug: 'invoices', label: 'Invoices settings', rows: ['Stripe Integration', 'Custom message'] },
  {
    slug: 'proposals',
    label: 'Proposals settings',
    rows: ['Proposal', 'Public proposal links', 'Google Earth Measurements', 'Attach PDF in email', 'Display logo only', 'Custom SMS', 'Custom Email', 'Custom thank you message', 'Google Reviews Badge', 'Custom public message'],
  },
  { slug: 'services', label: 'Services settings', rows: ['Stripe', 'Enable Subscriptions', 'Compare-at Pricing'] },
  { slug: 'inventory', label: 'Inventory settings', rows: ['Withdraw Stock', 'Add Inventory to Bookings', 'Track Materials on Bookings', 'Pin Orders Widget'] },
  { slug: 'teams', label: 'Teams settings', rows: ['Assets'] },
  {
    slug: 'messages',
    label: 'Messages settings (call recording, email signature)',
    rows: ['SMS & Voice', 'Record & transcribe calls', 'Email Integration'],
  },
  { slug: 'automations', label: 'Automate settings (delivery hours)' },
  {
    slug: 'crm',
    label: 'CRM settings',
    rows: ['Lead Forms', 'Lost Reason Tracking', 'Record Lost Reason', 'No Activity Alerts', 'Live Intake Forms', 'Enable Live Intake Forms'],
  },
  { slug: 'tasks', label: 'Docs settings', rows: ['Complete Documents from FAB Menu'] },
  { slug: 'marketing', label: 'Marketing settings (Google Ads)', rows: ['Google Ads'] },
  { slug: 'payroll', label: 'T-Sheets settings (overtime, kiosk)', rows: ['Clock in from FAB Menu', 'Clock in URL', 'Flat Rate Time Tracking'] },
  { slug: 'gift-cards', label: 'Gift Cards settings' },
];

export interface AssistantAnchor {
  id: string;
  label: string;
  description: string;
}

/**
 * Highlightable UI elements. Each id must have a matching
 * data-assistant-anchor="<id>" attribute on the element.
 */
export const ASSISTANT_ANCHORS: AssistantAnchor[] = [
  { id: 'menu-button', label: 'the menu button', description: 'The hamburger menu (top left) where all apps live. Visible on every screen.' },
  { id: 'blue-plus', label: 'the blue plus button', description: 'The blue create button (bottom right) for new bookings, proposals, events, clock-in. Visible on every screen.' },
  { id: 'voice-mode-button', label: 'the voice mode button', description: 'The purple voice-waves button in the bottom-right floating stack — starts/stops a live AI voice conversation. Turns red while voice mode is on.' },
  { id: 'profile-icon', label: 'the profile icon', description: 'The round profile icon (top right) — opens Business Settings, Subscription, AI Settings, Log out.' },
  { id: 'help-button', label: 'the help button', description: 'The question-mark help button in the top bar.' },
  { id: 'control-centre-button', label: 'the Control Centre button', description: 'The dashboard icon in the top bar that opens the Control Centre.' },
  { id: 'dialler-button', label: 'the phone dialler button', description: 'The phone icon in the top bar for making calls (only when Twilio is connected).' },
  { id: 'automations-create', label: 'the create button on Automations', description: 'The blue "+" on the Automations page — creates automations and AI agents. Only visible on the Automations screen.' },
  { id: 'top-up-tokens', label: 'the Top Up Tokens button', description: 'The purchase button on the AI Settings page. Only visible on the AI Settings screen.' },
];

/**
 * Compact catalog sent to the edge functions so they can build tool schemas.
 * Kept small — every token here rides along on each session/request.
 */
export function getAssistantActionCatalog() {
  return {
    destinations: ASSISTANT_DESTINATIONS.map(({ id, label, description }) => ({ id, label, description })),
    appSettings: ASSISTANT_APP_SETTINGS,
    anchors: ASSISTANT_ANCHORS,
    fabOptions: collectFabMenuOptions(),
    // Tools THIS build can execute. The server only offers a newer tool
    // (e.g. run_report) when the client lists it, so a function deploy can
    // land before the app build without the model calling a tool the
    // client does not know.
    actions: ASSISTANT_ACTION_NAMES,
  };
}

/**
 * The options currently in THIS user's blue-plus create menu (they vary by
 * installed apps, settings, and role). The rows are in the DOM even while the
 * menu is collapsed, so they can be read without opening it.
 */
function collectFabMenuOptions(): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const el of document.querySelectorAll('[data-assistant-fab-option]')) {
    const label = (el.getAttribute('data-assistant-fab-option') || '').trim().slice(0, 40);
    if (!label || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    labels.push(label);
    if (labels.length >= 24) break;
  }
  return labels;
}

export interface AssistantActionContext {
  /** Display names of installed apps (same list HelpModal already builds). */
  installedApps?: string[];
  /** Selected business id — required for live data queries (RLS-scoped). */
  businessId?: string;
  /** Which assistant surface triggered the action — for telemetry. */
  source?: 'text' | 'voice';
}

/**
 * True only when a human could actually see the element. A bounding box alone
 * is NOT enough: collapsed menus (e.g. the FAB create menu) hide their rows
 * with visibility/opacity, which still report a rect — and a programmatic
 * .click() on them would fire their handlers even though no human could.
 */
function isActuallyVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  const check = (el as HTMLElement & { checkVisibility?: (o?: object) => boolean }).checkVisibility;
  if (typeof check === 'function') {
    return check.call(el, { checkOpacity: true, checkVisibilityCSS: true, opacityProperty: true, visibilityProperty: true });
  }
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0;
}

/** Visible AND interactive — collapsed menu rows carry pointer-events: none. */
function isClickTarget(el: HTMLElement): boolean {
  return isActuallyVisible(el) && window.getComputedStyle(el).pointerEvents !== 'none';
}

/** First VISIBLE element for an anchor — mobile/desktop variants can coexist in the DOM. */
export function findVisibleAnchorElement(anchorId: string): HTMLElement | null {
  const candidates = document.querySelectorAll(`[data-assistant-anchor="${anchorId}"]`);
  for (const el of candidates) {
    if (!(el instanceof HTMLElement)) continue;
    if (isActuallyVisible(el)) return el;
  }
  return null;
}

/**
 * Wait for an anchor to become visible. Needed because a highlight often
 * directly follows an open_screen: the new page hasn't rendered yet when the
 * highlight executes.
 */
async function waitForVisibleAnchorElement(anchorId: string, timeoutMs = 4000): Promise<HTMLElement | null> {
  const deadline = Date.now() + timeoutMs;
  let el = findVisibleAnchorElement(anchorId);
  while (!el && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    el = findVisibleAnchorElement(anchorId);
  }
  return el;
}

/** Tags worth scanning when highlighting by visible label text. */
const TEXT_SEARCH_SELECTOR = 'button, a, label, h1, h2, h3, h4, p, span, div, td, th, li, [role="tab"], [role="menuitem"]';

/** Elements that natively respond to a click (customer picker rows are clickable divs). */
const CLICKABLE_SELECTOR = 'button, a, label, [role="button"], [role="tab"], [role="menuitem"], [onclick], [data-assistant-customer-row]';

/** Root of the app-settings modal when one is open (see AppSettingsModal). */
function getOpenSettingsModal(): HTMLElement | null {
  const el = document.querySelector('[data-assistant-modal="app-settings"]');
  return el instanceof HTMLElement ? el : null;
}

/**
 * The EXACT titles of the setting rows/sections rendered in the open settings
 * modal. Fed back to the model so it highlights real rows instead of guessing
 * names from the docs (e.g. "Bookings by Service Area" vs "Mobile Booking").
 */
export function collectSettingsModalLabels(): string[] {
  const modal = getOpenSettingsModal();
  if (!modal) return [];
  const labels: string[] = [];
  const seen = new Set<string>();
  // Row titles consistently use text-sm + font-semibold; h3 = section headers.
  for (const el of modal.querySelectorAll('h3, div.text-sm.font-semibold')) {
    const text = (el.textContent || '').trim();
    if (!text || text.length > 60) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    labels.push(text);
    if (labels.length >= 80) break;
  }
  return labels;
}

/**
 * Find the tightest visible element containing the given label text — lets the
 * assistant point at ANY named setting or button (e.g. rows inside app
 * settings modals) without a pre-registered anchor.
 */
export function findVisibleElementByText(text: string, root: ParentNode = document): HTMLElement | null {
  const exactNeedle = text.trim();
  const needle = exactNeedle.toLowerCase();
  if (needle.length < 2) return null;

  let best: HTMLElement | null = null;
  let bestLength = Infinity;
  let bestExact: HTMLElement | null = null;
  let bestExactScore = -1;
  for (const el of root.querySelectorAll(TEXT_SEARCH_SELECTOR)) {
    if (!(el instanceof HTMLElement)) continue;
    const content = (el.textContent || '').trim();
    if (!content || content.length > 300) continue;
    const contentLower = content.toLowerCase();
    if (!contentLower.includes(needle)) continue;
    if (!isActuallyVisible(el)) continue;
    // A node whose ENTIRE text is the needle always beats substring matches —
    // "Mobile Booking" must win over the longer "Mobile Bookings" header.
    // Among exact matches, prefer: matching case ("Josh S" vs "josh s" are
    // different records), then natively clickable elements over wrapper divs
    // (clicking the wrapper around a button does nothing), then the innermost
    // (querySelectorAll enumerates parents first, so ties go to the later).
    if (contentLower === needle) {
      const score = (content === exactNeedle ? 2 : 0) + (el.matches(CLICKABLE_SELECTOR) ? 1 : 0);
      if (score >= bestExactScore) {
        bestExact = el;
        bestExactScore = score;
      }
    }
    // Tightest match = the smallest element that still contains the text
    if (content.length < bestLength) {
      best = el;
      bestLength = content.length;
    }
  }
  const winner = bestExact || best;
  if (!winner) return null;

  // A bare text node inside a settings row is a poor highlight target —
  // prefer the interactive row/control around it when there is one.
  const container = winner.closest('label, button, a, [role="tab"], [role="menuitem"]');
  return container instanceof HTMLElement ? container : winner;
}

/**
 * Read a settings toggle's on/off state. Prefers proper switch semantics
 * (aria-checked), falls back to the modal's universal visual convention:
 * blue track = on, gray track = off. Null when the state can't be determined.
 */
function readToggleState(button: HTMLElement): boolean | null {
  const aria = button.getAttribute('aria-checked');
  if (aria === 'true') return true;
  if (aria === 'false') return false;
  const cls = button.className || '';
  if (/\bbg-blue-\d+/.test(cls)) return true;
  if (/\bbg-gray-\d+/.test(cls)) return false;
  return null;
}

/** The switch button inside a settings row, if the row has one. */
function findToggleButtonInRow(row: HTMLElement): HTMLElement | null {
  const explicit = row.querySelector('button[role="switch"]');
  if (explicit instanceof HTMLElement) return explicit;
  // Toggle tracks are the rounded-full pill buttons
  for (const btn of row.querySelectorAll('button')) {
    if (btn instanceof HTMLElement && /rounded-full/.test(btn.className) && readToggleState(btn) !== null) {
      return btn;
    }
  }
  return null;
}

/**
 * Modal-first search: while a settings modal is open it covers the screen, so
 * a match inside it must beat identical text on the page underneath.
 */
function findVisibleElementByTextScoped(text: string): HTMLElement | null {
  const modal = getOpenSettingsModal();
  if (modal) {
    const inModal = findVisibleElementByText(text, modal);
    if (inModal) return inModal;
  }
  return findVisibleElementByText(text);
}

/** True when the label exists in the DOM but only on hidden elements (collapsed menus). */
function elementExistsButHidden(text: string): boolean {
  const needle = text.trim().toLowerCase();
  if (needle.length < 2) return false;
  for (const el of document.querySelectorAll(`${TEXT_SEARCH_SELECTOR}, [aria-label]`)) {
    if (!(el instanceof HTMLElement)) continue;
    const content = `${(el.textContent || '').trim()} ${el.getAttribute('aria-label') || ''}`.trim().toLowerCase();
    if (!content || content.length > 300 || !content.includes(needle)) continue;
    if (!isClickTarget(el)) return true;
  }
  return false;
}

/** Elements the click primitive may press (aria-label matches count too). */
function findClickableByText(text: string): HTMLElement | null {
  const needle = text.trim().toLowerCase();
  if (needle.length < 2) return null;
  // Buttons labelled only via aria-label (icon buttons like the blue plus)
  for (const el of document.querySelectorAll('button[aria-label], a[aria-label], [role="button"][aria-label]')) {
    if (!(el instanceof HTMLElement)) continue;
    if (!(el.getAttribute('aria-label') || '').toLowerCase().includes(needle)) continue;
    if (isClickTarget(el)) return el;
  }
  const match = findVisibleElementByTextScoped(text);
  if (!match) return null;
  const clickable = match.closest(CLICKABLE_SELECTOR);
  const target = clickable instanceof HTMLElement ? clickable : match;
  return isClickTarget(target) ? target : null;
}

/** Scroll to an element and pulse the highlight ring so the user sees the AI act there. */
async function showActionTarget(el: HTMLElement, label: string, pauseMs = 700): Promise<void> {
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  window.dispatchEvent(new CustomEvent(ASSISTANT_HIGHLIGHT_EVENT, { detail: { element: el, label } }));
  await new Promise((resolve) => setTimeout(resolve, pauseMs));
}

/**
 * Find a form control by its label: <label> text, placeholder, or aria-label.
 * Searches the settings modal first when one is open.
 */
function findFieldByLabel(label: string): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null {
  const needle = label.trim().toLowerCase();
  if (needle.length < 2) return null;
  const roots: ParentNode[] = [];
  const modal = getOpenSettingsModal();
  if (modal) roots.push(modal);
  roots.push(document);

  for (const root of roots) {
    // Placeholder / aria-label direct matches
    for (const el of root.querySelectorAll('input, textarea, select')) {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) continue;
      if (!isActuallyVisible(el)) continue;
      const placeholder = ('placeholder' in el ? el.placeholder : '') || '';
      const aria = el.getAttribute('aria-label') || '';
      if (placeholder.toLowerCase().includes(needle) || aria.toLowerCase().includes(needle)) return el;
    }
    // <label> text → its control (htmlFor or descendant)
    for (const lab of root.querySelectorAll('label')) {
      if (!(lab instanceof HTMLLabelElement)) continue;
      if (!(lab.textContent || '').toLowerCase().includes(needle)) continue;
      const control = lab.htmlFor
        ? document.getElementById(lab.htmlFor)
        : lab.querySelector('input, textarea, select');
      if (
        (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement || control instanceof HTMLSelectElement) &&
        isActuallyVisible(control)
      ) {
        return control;
      }
    }
  }
  return null;
}

/** Set a controlled React input's value so state handlers actually fire. */
function setReactFieldValue(field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string): void {
  const proto = field instanceof HTMLTextAreaElement
    ? window.HTMLTextAreaElement.prototype
    : field instanceof HTMLSelectElement
      ? window.HTMLSelectElement.prototype
      : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(field, value);
  else (field as HTMLInputElement).value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Same waiting behavior as anchors — the modal/screen may still be opening. */
async function waitForVisibleElementByText(text: string, timeoutMs = 4000): Promise<HTMLElement | null> {
  const deadline = Date.now() + timeoutMs;
  let el = findVisibleElementByTextScoped(text);
  while (!el && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    el = findVisibleElementByTextScoped(text);
  }
  return el;
}

function findCurrentDestination(): AssistantDestination | undefined {
  const { pathname, search } = window.location;
  const full = pathname + search;
  // Prefer the most specific match (path+query beats bare path).
  let best: AssistantDestination | undefined;
  for (const dest of ASSISTANT_DESTINATIONS) {
    if (dest.path === full) return dest;
    const destPath = dest.path.split('?')[0];
    if (destPath === pathname && (!best || dest.path.length < best.path.length)) {
      best = dest;
    }
  }
  return best;
}

/** Latest fetched service catalog, so add_booking_service can resolve ids without refetching. */
let assistantServicesCache: { businessId: string; list: import('../services').Service[] } | null = null;

/** A fully resolved service line for a proposal offer, ready for the surface handler. */
export interface OfferServiceLine {
  service: import('../services').Service;
  priceOption: { id?: string; name: string; price: number; duration_minutes: number; pricing_type?: string; unit_price?: number; duration_per_unit?: number; compare_at_price?: number | null };
  priceOverride: number | null;
  titleOverride: string;
  descriptionOverride: string;
  durationMinutesOverride: number | null;
  quantity: number | null;
}

/** deposit_percentage arg → validated number, null (absent), or an error string. */
function parseDepositPercentage(raw: unknown): number | null | string {
  if (raw === undefined || raw === null || raw === '') return null;
  const pct = Number(raw);
  if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
    return 'deposit_percentage must be a percentage of the offer total between 1 and 100 (e.g. 50). For a dollar amount, convert it using the offer total first.';
  }
  return pct;
}

/**
 * Resolve raw offer service lines ({ service_id, price_option_id?, price?, ... })
 * against the catalog — same rules as add_booking_service: the price option is
 * required only when a service has several, per-unit options need a quantity,
 * and overrides never touch the catalog.
 */
async function resolveOfferServiceLines(
  businessId: string,
  raw: unknown,
): Promise<{ error: string } | { lines: OfferServiceLine[] }> {
  if (!Array.isArray(raw) || raw.length === 0) {
    return { error: 'Pass at least one service in "services" — call get_services first and pick the matching service_id(s).' };
  }
  let list = assistantServicesCache?.businessId === businessId ? assistantServicesCache.list : null;
  if (!list) {
    const { getServices } = await import('../services');
    list = await getServices(businessId);
    assistantServicesCache = { businessId, list };
  }
  const lines: OfferServiceLine[] = [];
  for (const entry of raw) {
    const item = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {};
    const serviceId = String(item.service_id || '').trim();
    if (!serviceId) return { error: 'Every service line needs a service_id from get_services.' };
    const service = list.find((s) => s.id === serviceId);
    if (!service) return { error: `No service with id "${serviceId}" — call get_services and use a service_id from the catalog.` };

    const options = service.price_options || [];
    const describeOption = (o: { id?: string; name: string; price: number }) => `"${o.name}" ($${o.price}) — price_option_id: ${o.id}`;
    const optionId = String(item.price_option_id || '').trim();
    let priceOption: OfferServiceLine['priceOption'] | null = null;
    if (optionId) {
      priceOption = options.find((o) => o.id === optionId) || null;
      if (!priceOption) {
        return { error: `Service "${service.name}" has no price option with that id. Its options: ${options.map(describeOption).join('; ')}.` };
      }
    } else if (options.length === 1) {
      priceOption = options[0];
    } else if (options.length === 0) {
      priceOption = { id: `svc-${service.id}`, name: 'Standard', price: 0, duration_minutes: 60 };
    } else {
      return { error: `Service "${service.name}" has ${options.length} price options: ${options.map(describeOption).join('; ')}. If the user's words clearly point to one, call again with that price_option_id; otherwise ask which they want.` };
    }

    const priceOverride = item.price !== undefined && item.price !== null && item.price !== '' ? Number(item.price) : null;
    if (priceOverride !== null && (!Number.isFinite(priceOverride) || priceOverride < 0)) {
      return { error: 'The custom price must be a number of dollars, e.g. 250.' };
    }
    const durationHours = item.duration_hours !== undefined && item.duration_hours !== null && item.duration_hours !== '' ? Number(item.duration_hours) : null;
    if (durationHours !== null && (!Number.isFinite(durationHours) || durationHours <= 0)) {
      return { error: 'duration_hours must be a positive number of hours, e.g. 2.5.' };
    }
    const quantity = item.quantity !== undefined && item.quantity !== null && item.quantity !== '' ? Number(item.quantity) : null;
    if (quantity !== null && (!Number.isFinite(quantity) || quantity <= 0)) {
      return { error: 'quantity must be a positive number of units.' };
    }
    const perUnit = priceOption.pricing_type === 'per_unit' || priceOption.pricing_type === 'per_sqft';
    if (perUnit && quantity === null && priceOverride === null) {
      return { error: `"${service.name}" (${priceOption.name}) is priced per ${priceOption.pricing_type === 'per_sqft' ? 'square foot' : 'unit'} at $${priceOption.unit_price ?? 0} — pass quantity (ask the user how many if they didn't say).` };
    }

    lines.push({
      service,
      priceOption,
      priceOverride,
      titleOverride: String(item.title ?? '').trim().slice(0, 200),
      descriptionOverride: String(item.description ?? '').trim().slice(0, 2000),
      durationMinutesOverride: durationHours !== null ? Math.round(durationHours * 60) : null,
      quantity,
    });
  }
  return { lines };
}


/** Confirmation chip shown in the help chat for an executed action. */
export interface AssistantActionChip {
  label: string;
  ok: boolean;
}

interface AssistantToolExecutor {
  /**
   * Perform the action and return the result string fed back to the model.
   * Result strings are written so the model can recover naturally on failure
   * (e.g. told to navigate first when a highlight target isn't on screen).
   */
  execute: (args: Record<string, unknown>, ctx: AssistantActionContext) => Promise<string> | string;
  /** Success-chip label for the help chat (omitted = "Done"). */
  chip?: (args: Record<string, unknown>, result: string) => string;
}

/**
 * The client tool registry — one entry per action the assistant can perform.
 * Adding a tool = one descriptor on the server (agentTools.ts) + one entry here
 * (+ a surface handler if it touches an open form).
 */
const TOOL_EXECUTORS = {
  open_screen: {
    chip: (args) => {
      const dest = ASSISTANT_DESTINATIONS.find((d) => d.id === String(args.destination));
      return `Opened ${dest?.label ?? 'screen'}`;
    },
    execute: async (args, ctx) => {
        const destId = String(args.destination || '');
        const dest = ASSISTANT_DESTINATIONS.find((d) => d.id === destId);
        if (!dest) {
          return `Unknown destination "${destId}". Valid destinations: ${ASSISTANT_DESTINATIONS.map((d) => d.id).join(', ')}.`;
        }
        if (
          dest.requiresApp &&
          Array.isArray(ctx.installedApps) &&
          ctx.installedApps.length > 0 &&
          !ctx.installedApps.some((n) => n.toLowerCase() === dest.requiresApp!.toLowerCase())
        ) {
          return `Cannot open ${dest.label} — the ${dest.requiresApp} app is not installed. It can be installed from Nexus Connect (menu → Nexus Connect → Nexus Apps tab).`;
        }
        deepLinkNavigate(dest.path);
        return `Opened ${dest.label}. The user is now looking at that screen.`;
    },
  },

  open_app_settings: {
    chip: (args) => {
      const entry = ASSISTANT_APP_SETTINGS.find((a) => a.slug === String(args.app));
      return `Opened ${entry?.label.replace(/ \(.*\)$/, '') ?? 'app settings'}`;
    },
    execute: async (args) => {
        const slug = String(args.app || '');
        const entry = ASSISTANT_APP_SETTINGS.find((a) => a.slug === slug);
        if (!entry) {
          return `Unknown app settings "${slug}". Valid values: ${ASSISTANT_APP_SETTINGS.map((a) => a.slug).join(', ')}.`;
        }
        window.dispatchEvent(new CustomEvent('openAppSettings', { detail: { appSlug: slug } }));
        // Wait for the modal to render, then read back the EXACT row titles so
        // the model highlights real settings instead of guessing their names.
        const deadline = Date.now() + 3000;
        let labels = collectSettingsModalLabels();
        while (labels.length === 0 && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 150));
          labels = collectSettingsModalLabels();
        }
        if (labels.length === 0) {
          return `Opened ${entry.label}. The settings modal is now on screen. (If the user lacks settings permission, nothing opened — ask them if they can see it.)`;
        }
        return `Opened ${entry.label}. The settings modal is now on screen. It contains EXACTLY these settings: ${labels.join(' | ')}. When pointing at one, call highlight_text with its name copied verbatim from this list — never a name you inferred.`;
    },
  },

  highlight: {
    chip: (args) => {
      const anchor = ASSISTANT_ANCHORS.find((a) => a.id === String(args.target));
      return `Highlighted ${anchor?.label.replace(/^the /, '') ?? 'element'}`;
    },
    execute: async (args) => {
        const anchorId = String(args.target || '');
        const anchor = ASSISTANT_ANCHORS.find((a) => a.id === anchorId);
        if (!anchor) {
          return `Unknown highlight target "${anchorId}". Valid targets: ${ASSISTANT_ANCHORS.map((a) => a.id).join(', ')}.`;
        }
        const el = await waitForVisibleAnchorElement(anchorId);
        if (!el) {
          const current = findCurrentDestination();
          return `${anchor.label} is not visible on the current screen (${current ? current.label : window.location.pathname}). Open the right screen first, then highlight again.`;
        }
        window.dispatchEvent(
          new CustomEvent(ASSISTANT_HIGHLIGHT_EVENT, { detail: { anchor: anchorId, label: anchor.label } }),
        );
        return `Now visually highlighting ${anchor.label} for the user — refer to it as the element that's glowing.`;
    },
  },

  highlight_text: {
    chip: (args) => `Highlighted "${String(args.text ?? 'element')}"`,
    execute: async (args) => {
        const text = String(args.text || '').trim().slice(0, 120);
        if (text.length < 2) {
          return 'highlight_text needs the visible label text to point at (at least 2 characters).';
        }
        const el = await waitForVisibleElementByText(text);
        if (!el) {
          const modalLabels = collectSettingsModalLabels();
          if (modalLabels.length > 0) {
            return `Nothing in the open settings modal matches "${text}". The modal contains EXACTLY these settings: ${modalLabels.join(' | ')}. Call highlight_text again with the right one copied verbatim.`;
          }
          const current = findCurrentDestination();
          return `Nothing on screen matches "${text}" (currently on ${current ? current.label : window.location.pathname}). Check the wording matches the visible label, or open the right screen/settings modal first.`;
        }
        window.dispatchEvent(
          new CustomEvent(ASSISTANT_HIGHLIGHT_EVENT, { detail: { element: el, label: text } }),
        );
        return `Now visually highlighting "${text}" for the user — refer to it as the element that's glowing.`;
    },
  },

  set_setting: {
    chip: (args, result) =>
      /already/i.test(result)
        ? `"${String(args.setting ?? 'Setting')}" was already ${String(args.state ?? '')}`
        : `Turned ${String(args.state ?? '')} "${String(args.setting ?? 'setting')}"`,
    execute: async (args) => {
        const slug = String(args.app || '');
        const settingLabel = String(args.setting || '').trim().slice(0, 80);
        const stateArg = String(args.state || '').toLowerCase();
        if (stateArg !== 'on' && stateArg !== 'off') {
          return 'set_setting needs state: "on" or "off".';
        }
        const desired = stateArg === 'on';
        const entry = ASSISTANT_APP_SETTINGS.find((a) => a.slug === slug);
        if (!entry) {
          return `Unknown app "${slug}". Valid values: ${ASSISTANT_APP_SETTINGS.map((a) => a.slug).join(', ')}.`;
        }
        if (settingLabel.length < 2) {
          return 'set_setting needs the visible setting name exactly as listed for that app.';
        }

        // 1. Make sure the right settings modal is on screen (visible to the user)
        let modal = getOpenSettingsModal();
        let row = modal ? findVisibleElementByText(settingLabel, modal) : null;
        if (!row) {
          window.dispatchEvent(new CustomEvent('openAppSettings', { detail: { appSlug: slug } }));
          const deadline = Date.now() + 4000;
          while (!row && Date.now() < deadline) {
            await new Promise((resolve) => setTimeout(resolve, 200));
            modal = getOpenSettingsModal();
            row = modal ? findVisibleElementByText(settingLabel, modal) : null;
          }
        }
        if (!row) {
          const labels = collectSettingsModalLabels();
          return labels.length > 0
            ? `Could not find "${settingLabel}" in the ${entry.label} modal. It contains EXACTLY these settings: ${labels.join(' | ')}. Retry with the right name copied verbatim.`
            : `The ${entry.label} modal did not open (the user may lack settings permission). Ask them to open it manually.`;
        }

        // 2. Show the user where we are about to act: scroll there + glow
        const rowContainer = (row.closest('label') as HTMLElement) || row;
        rowContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.dispatchEvent(
          new CustomEvent(ASSISTANT_HIGHLIGHT_EVENT, { detail: { element: rowContainer, label: settingLabel } }),
        );
        await new Promise((resolve) => setTimeout(resolve, 900));

        // 3. Operate the REAL toggle — its own onClick persists the change and
        // dispatches the app's SettingChanged event, exactly like a user tap.
        const toggle = findToggleButtonInRow(rowContainer);
        if (!toggle) {
          return `"${settingLabel}" is not an on/off toggle (it may open a configuration screen instead). Highlighted it — guide the user through it manually.`;
        }
        const before = readToggleState(toggle);
        if (before === desired) {
          return `"${settingLabel}" is already ${stateArg} — no change needed. It's highlighted on screen so the user can see it.`;
        }
        toggle.click();
        await new Promise((resolve) => setTimeout(resolve, 600));
        const after = readToggleState(toggle);
        if (after === desired) {
          return `Done — turned "${settingLabel}" ${stateArg} right on the user's screen (the setting is glowing). The change is saved.`;
        }
        return `Clicked the "${settingLabel}" toggle but could not confirm the new state — ask the user to check whether it's now ${stateArg}.`;
    },
  },

  click_element: {
    chip: (args) => `Clicked "${String(args.text ?? 'element')}"`,
    execute: async (args) => {
        const text = String(args.text || '').trim().slice(0, 120);
        if (text.length < 2) {
          return 'click_element needs the visible label (or aria-label) of the element to press.';
        }
        // Wait briefly — the target often appears right after a previous action
        const deadline = Date.now() + 3000;
        let target = findClickableByText(text);
        while (!target && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          target = findClickableByText(text);
        }
        if (!target) {
          if (elementExistsButHidden(text)) {
            return `"${text}" exists but is currently hidden inside a closed menu or panel — open whatever reveals it first (e.g. the booking options only appear AFTER clicking "Create new"), then click it.`;
          }
          const current = findCurrentDestination();
          return `Nothing clickable on screen matches "${text}" (currently on ${current ? current.label : window.location.pathname}). Use look_at_screen to see what's actually there.`;
        }
        // Ambiguity guard: several picker rows can share the exact same
        // customer name — clicking the bare name would silently take the
        // first one. Force a disambiguation instead (a full "name (email)"
        // label bypasses this because it identifies one row).
        if (document.querySelector('[data-select-customer-modal]')) {
          const needleLc = text.trim().toLowerCase();
          const sameName = Array.from(document.querySelectorAll('[data-assistant-customer-row]'))
            .filter((el): el is HTMLElement => el instanceof HTMLElement && isActuallyVisible(el))
            .map((el) => ({
              name: (el.getAttribute('data-assistant-customer-row') || '').trim(),
              detail: (el.getAttribute('data-assistant-customer-detail') || '').trim(),
            }))
            .filter((r) => r.name.toLowerCase() === needleLc);
          if (sameName.length > 1) {
            const opts = sameName
              .slice(0, 6)
              .map((r) => (r.detail ? `${r.name} (${r.detail})` : r.name))
              .join('; ');
            return `NOT CLICKED: ${sameName.length} customers in the list are all named "${text}" — picking one blindly would likely be the wrong record. Ask the user which one they mean, citing these options: ${opts}. Then click_element with the full label, e.g. "${sameName[0].detail ? `${sameName[0].name} (${sameName[0].detail})` : sameName[0].name}".`;
          }
        }
        await showActionTarget(target, text);
        target.click();
        await new Promise((resolve) => setTimeout(resolve, 400));
        // A non-native clickable (bare div/span match) may have no handler —
        // warn the model so it verifies instead of assuming progress.
        return target.matches(CLICKABLE_SELECTOR)
          ? `Clicked "${text}" on the user's screen. Use look_at_screen if you need to see what happened.`
          : `Clicked "${text}", but it may not be an interactive element — use look_at_screen to confirm something actually changed before continuing.`;
    },
  },

  fill_field: {
    chip: (args) => `Filled in "${String(args.label ?? 'field')}"`,
    execute: async (args) => {
        const label = String(args.label || '').trim().slice(0, 120);
        const value = String(args.value ?? '').slice(0, 500);
        if (label.length < 2) {
          return 'fill_field needs the field\'s visible label, placeholder, or aria-label.';
        }
        const deadline = Date.now() + 3000;
        let field = findFieldByLabel(label);
        while (!field && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          field = findFieldByLabel(label);
        }
        if (!field) {
          const clickable = findClickableByText(label);
          if (clickable) {
            return `"${label}" is a button, not an input field — use click_element to press it (it may open a picker with a search field inside).`;
          }
          return `No input field on screen matches "${label}". Use look_at_screen to see the form's actual field names.`;
        }
        await showActionTarget(field, label);
        field.focus();
        setReactFieldValue(field, value);
        // Customer picker searches get ground truth appended: what the list
        // actually shows now, so the model broadens/picks/asks on facts
        // instead of guessing (or wrongly jumping to "New Customer").
        if (document.querySelector('[data-select-customer-modal]')) {
          const readRows = () =>
            Array.from(document.querySelectorAll('[data-assistant-customer-row]'))
              .filter((el): el is HTMLElement => el instanceof HTMLElement && isActuallyVisible(el))
              .map((el) => ({
                name: el.getAttribute('data-assistant-customer-row') || '',
                detail: el.getAttribute('data-assistant-customer-detail') || '',
              }))
              .filter((r) => r.name);
          const describe = (r: { name: string; detail: string }) => (r.detail ? `${r.name} (${r.detail})` : r.name);
          const before = readRows().map(describe).join('|');
          const resultsDeadline = Date.now() + 2500;
          await new Promise((resolve) => setTimeout(resolve, 500));
          while (Date.now() < resultsDeadline && readRows().map(describe).join('|') === before) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
          const rows = readRows();
          if (rows.length === 0) {
            return `Typed "${value}" into "${label}" — the customer list shows NO results for this term. Broaden the search yourself (e.g. first name only). Do NOT click "New Customer" unless the user explicitly asked to create a new customer.`;
          }
          const shown = rows.slice(0, 8).map(describe);
          const listing = `${shown.join('; ')}${rows.length > shown.length ? '; …' : ''}`;
          // An exact name match is the answer — the model must not stop to ask
          // "did you mean josh s?" when the user literally said "josh s".
          const exactMatches = rows.filter((r) => r.name.toLowerCase() === value.trim().toLowerCase());
          if (exactMatches.length === 1) {
            return `Typed "${value}" into "${label}" — the list shows ${rows.length} result(s): ${listing}. "${exactMatches[0].name}" EXACTLY matches the search — click it now with click_element, do NOT stop to ask.`;
          }
          if (exactMatches.length > 1) {
            return `Typed "${value}" into "${label}" — the list shows ${rows.length} result(s): ${listing}. ${exactMatches.length} customers share this exact name — ask the user which one they mean, citing the emails/phones shown. Then click the chosen one with click_element using its full label, e.g. "${describe(exactMatches[0])}".`;
          }
          return `Typed "${value}" into "${label}" — the customer list now shows ${rows.length} result(s): ${listing}. If one clearly matches what the user said, click it with click_element and tell them which you picked; ask only if several are equally plausible.`;
        }
        return `Typed "${value}" into the "${label}" field on the user's screen. Use look_at_screen to verify the form state if needed.`;
    },
  },

  query_business_data: {
    execute: async (args, ctx) => {
      const { runBusinessDataQuery } = await import('./dataQueries');
      const query = String(args.query || '');
      const params = args.params && typeof args.params === 'object' ? (args.params as Record<string, unknown>) : {};
      return runBusinessDataQuery(query, params, ctx.businessId);
    },
  },

  run_report: {
    chip: (args) => {
      const match = /^FROM\s+([a-z_]+)/i.exec(String(args.query || ''));
      return `Ran the ${match ? match[1].replace(/_/g, ' ') : ''} report`.replace(/\s+/g, ' ').trim();
    },
    execute: async (args, ctx) => {
      const { runAssistantReport } = await import('./reportQuery');
      return runAssistantReport(String(args.query || ''), ctx.businessId);
    },
  },

  lookup_customer: {
    chip: (args) => `Looked up "${String(args.query ?? 'customer')}"`,
    execute: async (args, ctx) => {
        const query = String(args.query || '').trim().slice(0, 120);
        if (query.length < 2) return 'lookup_customer needs a name, email, or phone to search for.';
        if (!ctx.businessId) return 'No business selected — cannot look up customers right now.';
        const { lookupCustomers, showGhostCustomers } = await import('./customerLookup');
        const { exact, similar } = await lookupCustomers(ctx.businessId, query);
        const describe = (c: { name: string; email: string; phone: string; id: string }) =>
          `${c.name}${c.email ? `, ${c.email}` : ''}${c.phone ? `, ${c.phone}` : ''} — customer_id: ${c.id}`;

        if (exact.length === 1) {
          return `Found exactly one match for "${query}": ${describe(exact[0])}. Use this customer_id directly (e.g. in start_booking) — no need to ask.`;
        }
        if (exact.length > 1) {
          showGhostCustomers(exact, `${exact.length} customers named "${exact[0].name}"`);
          return `${exact.length} customers share the exact name "${query}" — they are now displayed on the user's screen as ghost cards. Ask which one they mean (cite emails/phones):\n${exact.map(describe).join('\n')}`;
        }
        if (similar.length === 1) {
          return `No exact match for "${query}", but exactly ONE similar customer: ${describe(similar[0])}. This is almost certainly who the user meant (voice transcription is often slightly off) — USE this customer_id and simply tell the user which customer you picked. Only ask first if the name is clearly a DIFFERENT person, not just a spelling variation.`;
        }
        if (similar.length > 1) {
          showGhostCustomers(similar, `Possible matches for "${query}"`);
          return `No exact match for "${query}", but ${similar.length} similar customers — now displayed on the user's screen as ghost cards. If one clearly matches what the user said, use it and say so; otherwise ask which:\n${similar.map(describe).join('\n')}`;
        }
        return `No customers found matching "${query}" — the lookup already retried broadened spellings and prefixes. If it came from voice, try ONE alternate phonetic spelling; otherwise tell the user it's not in their customers and ask whether to create a new customer.`;
    },
  },

  call_person: {
    chip: (args) => `Calling ${String(args.name || args.phone || 'someone')}`,
    execute: async (args, ctx) => {
        let phoneArg = String(args.phone || '').trim();
        let nameArg = String(args.name || '').trim();
        if (!phoneArg && !nameArg) return 'error: call_person needs a name or a phone number.';

        // The model sometimes passes a number as the name — dial it anyway.
        if (!phoneArg && /^[\d\s()+.\-]+$/.test(nameArg) && nameArg.replace(/\D/g, '').length >= 7) {
          phoneArg = nameArg;
          nameArg = '';
        }

        // A raw number (e.g. from a conversation card or missed call) dials directly.
        if (phoneArg) {
          const digits = phoneArg.replace(/\D/g, '');
          if (digits.length < 7) return `error: "${phoneArg}" is not a dialable phone number.`;
          const result = await askSurface('phone', 'call', { phone: phoneArg, displayName: nameArg }, 15000);
          return result ?? 'error: the phone system did not respond — calling may not be available right now.';
        }

        if (!ctx.businessId) return 'No business selected — cannot look up who to call.';
        // Recap cards render as "josh s (their@email.com)" and the model passes
        // that verbatim — the embedded email pinpoints ONE person among
        // same-named customers.
        const emailInName = (nameArg.match(/[^\s()<>]+@[^\s()<>]+\.[^\s()<>]+/) || [])[0]?.toLowerCase() || '';
        const cleanName = nameArg.replace(/\([^)]*\)/g, ' ').replace(/[^\s@]+@[^\s@]+/g, ' ').replace(/\s+/g, ' ').trim() || nameArg;
        const { lookupCustomers, showGhostCustomers } = await import('./customerLookup');
        const { exact, similar } = await lookupCustomers(ctx.businessId, cleanName);
        const pool = exact.length > 0 ? exact : similar;
        let candidates = pool.filter((c) => c.phone);
        if (emailInName && candidates.length > 1) {
          const byEmail = candidates.filter((c) => (c.email || '').toLowerCase().trim() === emailInName);
          if (byEmail.length >= 1) candidates = byEmail;
        }
        if (candidates.length === 0) {
          return pool.length > 0
            ? `error: found ${pool[0].name} but their profile has no phone number saved — cannot call them.`
            : `error: no customer matches "${nameArg}". If you have their number from a conversation, call again with it as phone.`;
        }
        if (candidates.length > 1) {
          showGhostCustomers(candidates, 'Who should I call?');
          return (
            `${candidates.length} customers match "${nameArg}" — do NOT dial yet, ask which one (refer to them by name and number, never ids):\n` +
            candidates.map((c) => `${c.name} — ${c.phone}`).join('\n')
          );
        }
        const target = candidates[0];
        const result = await askSurface(
          'phone', 'call',
          { phone: target.phone, customerId: target.id, displayName: target.name },
          15000,
        );
        return result ?? 'error: the phone system did not respond — calling may not be available right now.';
    },
  },

  create_customer: {
    chip: (args) => `Created customer ${[args.first_name, args.last_name].filter(Boolean).join(' ') || ''}`.trim(),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot create customers right now.';
        const first = String(args.first_name || '').trim();
        const last = String(args.last_name || '').trim();
        const phone = String(args.phone || '').trim();
        const email = String(args.email || '').trim().toLowerCase();
        const address = String(args.address || '').trim();
        const missing: string[] = [];
        if (!first) missing.push('a first name');
        if (!last) missing.push('a last name');
        if (!phone && !email) missing.push('a phone number or email');
        if (missing.length > 0) {
          return `error: still needs ${missing.join(', ')}. Check the conversation (including call transcripts) for it first; only ask the user for what the person never gave.`;
        }
        try {
          const { createCustomer } = await import('../customers');
          const customer = await createCustomer({
            business_id: ctx.businessId,
            type: 'person',
            first_name: first,
            last_name: last,
            email,
            phone,
            address,
          } as never);
          const contact = [phone, email].filter(Boolean).join(', ');
          return (
            `Created customer ${customer.first_name} ${customer.last_name}${contact ? ` (${contact})` : ''}. ` +
            'Their past messages and calls now show under their name automatically. ' +
            'Tell the user in ONE short sentence — refer to the customer by name, never by id.'
          );
        } catch (err) {
          // Duplicate/archived errors from createCustomer already name the
          // existing customer — relay them so the model can tell the user.
          return `error: ${err instanceof Error ? err.message : 'could not create the customer.'}`;
        }
    },
  },

  start_booking: {
    chip: (args) => `Opened ${String(args.type) === 'mobile' ? 'Mobile' : 'Location'} Booking form`,
    execute: async (args) => {
        const type = String(args.type || '').toLowerCase() === 'mobile' ? 'mobile' : 'location';
        const optionLabel = type === 'mobile' ? 'Mobile Booking' : 'Location Booking';
        if (!document.querySelector(`[data-assistant-fab-option="${optionLabel}"]`)) {
          return `"${optionLabel}" is not enabled in this user's create menu — its Calendar app setting is off. Turn it on with set_setting (app=calendar, setting="${optionLabel}", state=on) or ask the user first.`;
        }
        let customer: unknown = null;
        const customerId = String(args.customer_id || '').trim();
        if (customerId) {
          const { getCachedCustomer, clearGhostCustomers } = await import('./customerLookup');
          customer = getCachedCustomer(customerId);
          if (!customer) {
            try {
              const { getCustomer } = await import('../customers');
              customer = await getCustomer(customerId);
            } catch {
              customer = null;
            }
          }
          clearGhostCustomers();
          if (!customer) {
            return `customer_id "${customerId}" was not found — run lookup_customer first and use the id it returns.`;
          }
        }
        window.dispatchEvent(new CustomEvent('assistant:createBooking', { detail: { type, customer } }));
        const heading = type === 'mobile' ? 'Create Mobile Booking' : 'Create Booking';
        const el = await waitForVisibleElementByText(heading, 5000);
        const name = customer && typeof customer === 'object'
          ? `${(customer as { first_name?: string }).first_name || ''} ${(customer as { last_name?: string }).last_name || ''}`.trim()
          : '';
        if (!el) {
          return `Tried to open the ${optionLabel} form but could not confirm it appeared — use look_at_screen to check what's on screen.`;
        }
        if (!name) {
          return `The ${optionLabel} form is now open on the user's screen (no customer preselected). If the user named a customer, use lookup_customer + start_booking with its customer_id next time; otherwise continue in the form.`;
        }
        // Customer Source: repeat customers auto-fill ("Repeat Customer"); new
        // customers leave it empty and the AI must ask where they came from.
        let sourceNote = '';
        let vehicleNote = '';
        if (type === 'location') {
          const deadline = Date.now() + 2500;
          let src = '';
          while (Date.now() < deadline) {
            src = document.querySelector('[data-assistant-customer-source]')?.getAttribute('data-assistant-customer-source') || '';
            if (src) break;
            await new Promise((resolve) => setTimeout(resolve, 250));
          }
          sourceNote = src
            ? ` Customer Source auto-filled: "${src}" — no need to ask about it.`
            : ' The Customer Source field did NOT auto-fill (new customer). Ask the user right away where this customer came from, then use set_customer_source with their answer.';

          // Vehicles matter for automotive businesses: services attach to one.
          try {
            const { supabase } = await import('../supabase');
            const { data: veh } = await supabase
              .from('customer_vehicles')
              .select('id, make, model, year, is_primary')
              .eq('customer_id', customerId)
              .order('is_primary', { ascending: false });
            if (veh && veh.length > 0) {
              const listing = veh
                .map((v) => `${v.model}${v.is_primary ? ' (main)' : ''}`)
                .join(', ');
              vehicleNote = ` Vehicles on file: ${listing}. If this business is automotive, services attach to the main vehicle automatically — don't ask about the vehicle unless the user names a different one, and if they do, refer to vehicles by MODEL only (never ids).`;
            } else {
              vehicleNote = ' This customer has NO vehicles on file — if this business is automotive, ask for the vehicle (make, model, year) and use create_vehicle before adding services.';
            }
          } catch { /* vehicles note is best-effort */ }
        }
        return `The ${optionLabel} form is now open on the user's screen with ${name} preselected as the customer.${sourceNote}${vehicleNote} Continue with the rest of the booking (services, date, time) as the user requested.`;
    },
  },

  set_customer_source: {
    chip: (args, result) => {
      if (/^Created/i.test(result)) return `Created source "${String(args.source ?? '')}"`;
      if (/now set/i.test(result)) return `Set source "${String(args.source ?? '')}"`;
      return 'Checked customer sources';
    },
    execute: async (args, ctx) => {
        const raw = String(args.source || '').trim().slice(0, 60);
        const createNew = args.create === true || String(args.create) === 'true';
        if (!raw) return 'set_customer_source needs the source name.';
        if (!ctx.businessId) return 'No business selected — cannot manage customer sources right now.';
        const field = document.querySelector('[data-assistant-customer-source]');
        if (!field) {
          return 'No booking form with a Customer Source field is open (it appears once a customer is selected). Open the booking with start_booking first.';
        }
        const { CustomerSourcesService } = await import('../customerSources');
        const norm = (s: string) => s.toLowerCase().trim();
        const stored = await CustomerSourcesService.getCustomerSources(ctx.businessId);
        // System sources the form always offers even when not in the table
        const all = [...stored];
        for (const sys of [
          { id: 'repeat-customer', name: 'Repeat Customer' },
          { id: 'referral', name: 'Referral' },
        ]) {
          if (!all.some((s) => norm(s.name) === norm(sys.name))) {
            all.push({ ...sys, business_id: ctx.businessId, is_active: true, created_at: '', updated_at: '' });
          }
        }

        let match = all.find((s) => norm(s.name) === norm(raw));
        // Loose text match only helps when NOT explicitly creating
        if (!match && !createNew) {
          match = all.find((s) => norm(s.name).includes(norm(raw)) || norm(raw).includes(norm(s.name)));
        }

        if (!match && createNew) {
          const titled = raw.replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
          const created = await CustomerSourcesService.createCustomerSource({ business_id: ctx.businessId, name: titled });
          window.dispatchEvent(new CustomEvent('assistant:setCustomerSource', { detail: { source: created } }));
          return `Created a new customer source "${created.name}" and selected it in the booking form.`;
        }
        if (!match) {
          const names = all.map((s) => `"${s.name}"`).join(', ');
          return `No existing source is a text match for "${raw}". Existing sources: ${names}. Decide like a human: if one of these logically covers "${raw}" (e.g. "facebook" is covered by "Social Media"), call set_customer_source again with that EXACT existing name. Only if none logically fits — or the user explicitly wants a dedicated "${raw}" option — call again with create=true.`;
        }
        window.dispatchEvent(new CustomEvent('assistant:setCustomerSource', { detail: { source: match } }));
        await new Promise((resolve) => setTimeout(resolve, 200));
        const nowSet = document.querySelector('[data-assistant-customer-source]')?.getAttribute('data-assistant-customer-source') || '';
        return nowSet === match.name
          ? `Customer Source is now set to "${match.name}" in the booking form.`
          : `Selected source "${match.name}" but could not confirm the field updated — look_at_screen to verify.`;
    },
  },

  get_services: {
    chip: () => 'Checked the service catalog',
    execute: async (_args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read services right now.';
        const { getServices } = await import('../services');
        const services = await getServices(ctx.businessId);
        assistantServicesCache = { businessId: ctx.businessId, list: services };
        if (services.length === 0) {
          return 'This business has no services yet. The user can create them under Manage → Services.';
        }
        // Add-ons live in their own table; one batched fetch for the catalog
        const addonsByService = new Map<string, Array<{ name: string; price: number }>>();
        try {
          const { supabase } = await import('../supabase');
          const { data: addonRows } = await supabase
            .from('service_addons')
            .select('service_id, name, price')
            .in('service_id', services.map((s) => s.id));
          for (const row of addonRows || []) {
            const list = addonsByService.get(row.service_id) || [];
            list.push({ name: row.name, price: row.price });
            addonsByService.set(row.service_id, list);
          }
        } catch { /* catalog still useful without add-ons */ }

        const compact = services.map((s) => ({
          service_id: s.id,
          name: s.name,
          category: s.category?.name || undefined,
          description: (s.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 140) || undefined,
          price_options: (s.price_options || []).map((o) => ({
            price_option_id: o.id,
            name: o.name,
            price: o.price,
            duration_minutes: o.duration_minutes,
          })),
          addons: addonsByService.get(s.id),
        }));
        return `Full service catalog (${services.length} services, prices in dollars). Pick the service that best matches the user's words (their phrasing rarely matches the name exactly — "front end ppf" matches a "Front End PPF Package") and use its service_id in add_booking_service or add_proposal_offer, plus price_option_id when a service has several options. If NOTHING in the catalog does what the user described, never substitute the closest unrelated service — tell the user nothing matches and ask:\n${JSON.stringify(compact)}`;
    },
  },

  add_booking_service: {
    chip: (_args, result) => {
      const added = result.match(/^Added "([^"]+)"/);
      return added ? `Added "${added[1]}" to the booking` : 'Added a service to the booking';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot add services right now.';
        const serviceId = String(args.service_id || '').trim();
        if (!serviceId) return 'add_booking_service needs a service_id — call get_services first and pick the matching service.';
        let list = assistantServicesCache?.businessId === ctx.businessId ? assistantServicesCache.list : null;
        if (!list) {
          const { getServices } = await import('../services');
          list = await getServices(ctx.businessId);
          assistantServicesCache = { businessId: ctx.businessId, list };
        }
        const service = list.find((s) => s.id === serviceId);
        if (!service) return `No service with id "${serviceId}" — call get_services and use a service_id from the catalog.`;

        const options = service.price_options || [];
        const describeOption = (o: { id?: string; name: string; price: number }) => `"${o.name}" ($${o.price}) — price_option_id: ${o.id}`;
        const optionId = String(args.price_option_id || '').trim();
        let priceOption: unknown = null;
        if (optionId) {
          priceOption = options.find((o) => o.id === optionId) || null;
          if (!priceOption) {
            return `Service "${service.name}" has no price option with that id. Its options: ${options.map(describeOption).join('; ')}.`;
          }
        } else if (options.length === 1) {
          priceOption = options[0];
        } else if (options.length === 0) {
          priceOption = { id: `svc-${service.id}`, service_id: service.id, name: 'Standard', price: 0, duration_minutes: 60 };
        } else {
          return `Service "${service.name}" has ${options.length} price options: ${options.map(describeOption).join('; ')}. If the user's words clearly point to one, call again with that price_option_id; otherwise ask which they want.`;
        }

        // Per-booking overrides ("it's gonna be 700 this time") — applied to
        // this booking's copy only, the catalog price never changes.
        const priceOverride = args.price !== undefined && args.price !== null && args.price !== '' ? Number(args.price) : null;
        if (priceOverride !== null && (!Number.isFinite(priceOverride) || priceOverride < 0)) {
          return 'The custom price must be a number of dollars, e.g. 700.';
        }
        const durationHours = args.duration_hours !== undefined && args.duration_hours !== null && args.duration_hours !== '' ? Number(args.duration_hours) : null;
        if (durationHours !== null && (!Number.isFinite(durationHours) || durationHours <= 0)) {
          return 'duration_hours must be a positive number of hours, e.g. 2.5.';
        }

        const result = await askSurface(
          'booking',
          'addService',
          {
            service,
            priceOption,
            priceOverride,
            durationMinutesOverride: durationHours !== null ? Math.round(durationHours * 60) : null,
            vehicleQuery: String(args.vehicle || '').trim(),
            vehicleId: String(args.vehicle_id || '').trim(),
          },
          6000,
        );
        return result ?? 'The booking form did not respond — is it open? Use start_booking first, then add the service again.';
    },
  },

  update_booking_service: {
    chip: (_args, result) => {
      const updated = result.match(/^Updated "([^"]+)"/);
      return updated ? `Updated "${updated[1]}" on the booking` : 'Updated a booking service';
    },
    execute: async (args) => {
        const hasNewName = typeof args.new_name === 'string' && args.new_name.trim() !== '';
        const hasNewDescription = typeof args.new_description === 'string' && args.new_description.trim() !== '';
        const newPrice = args.new_price !== undefined && args.new_price !== null && args.new_price !== '' ? Number(args.new_price) : null;
        if (newPrice !== null && (!Number.isFinite(newPrice) || newPrice < 0)) {
          return 'new_price must be a number of dollars, e.g. 700.';
        }
        const newDurationHours = args.new_duration_hours !== undefined && args.new_duration_hours !== null && args.new_duration_hours !== '' ? Number(args.new_duration_hours) : null;
        if (newDurationHours !== null && (!Number.isFinite(newDurationHours) || newDurationHours <= 0)) {
          return 'new_duration_hours must be a positive number of hours, e.g. 2.5.';
        }
        if (!hasNewName && !hasNewDescription && newPrice === null && newDurationHours === null) {
          return 'update_booking_service needs at least one change: new_name, new_description, new_price, or new_duration_hours.';
        }
        const result = await askSurface(
          'booking',
          'updateService',
          {
            serviceQuery: String(args.service || '').trim(),
            newName: hasNewName ? String(args.new_name).trim().slice(0, 120) : null,
            newDescription: hasNewDescription ? String(args.new_description).trim().slice(0, 1000) : null,
            newPrice,
            newDurationMinutes: newDurationHours !== null ? Math.round(newDurationHours * 60) : null,
          },
          5000,
        );
        return result ?? 'The booking form did not respond — is it open? Use start_booking first.';
    },
  },

  set_booking_schedule: {
    chip: (args) => `Scheduled for ${String(args.date ?? '')} ${String(args.time ?? '')}`.trim(),
    execute: async (args) => {
        const date = String(args.date || '').trim();
        const time = String(args.time || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return 'date must be YYYY-MM-DD — resolve relative dates ("tomorrow", "march 3rd") yourself from the current date, never ask the user for a format.';
        }
        if (!/^\d{1,2}:\d{2}$/.test(time)) {
          return 'time must be HH:MM in 24-hour format. If the user never gave a time of day, ask them for one.';
        }
        const dropDate = String(args.drop_off_date || '').trim() || date;
        const dropTime = String(args.drop_off_time || '').trim() || time;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dropDate) || !/^\d{1,2}:\d{2}$/.test(dropTime)) {
          return 'drop_off_date must be YYYY-MM-DD and drop_off_time HH:MM (24h).';
        }
        const endDate = String(args.end_date || '').trim();
        const endTime = String(args.end_time || '').trim();
        if (endDate && !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
          return 'end_date must be YYYY-MM-DD (only pass it when the user explicitly named an end date).';
        }
        if (endTime && !/^\d{1,2}:\d{2}$/.test(endTime)) {
          return 'end_time must be HH:MM in 24-hour format.';
        }
        const result = await askSurface(
          'booking',
          'setSchedule',
          {
            startDate: date,
            startTime: time.padStart(5, '0'),
            dropOffDate: dropDate,
            dropOffTime: dropTime.padStart(5, '0'),
            ...(endDate ? { endDate, endTime: endTime ? endTime.padStart(5, '0') : '' } : {}),
          },
          5000,
        );
        return result ?? 'The booking form did not respond — is it open? Use start_booking first.';
    },
  },

  create_vehicle: {
    chip: (args) => `Added vehicle ${String(args.make ?? '')} ${String(args.model ?? '')}`.trim(),
    execute: async (args) => {
        const make = String(args.make || '').trim();
        const model = String(args.model || '').trim();
        if (!make || !model) return 'create_vehicle needs at least make and model (plus year if the user gave it).';
        const result = await askSurface(
          'booking',
          'createVehicle',
          { make, model, year: args.year, color: args.color },
          8000,
        );
        return result ?? 'The booking form did not respond — is it open? Use start_booking first, then create the vehicle again.';
    },
  },

  set_booking_notes: {
    chip: () => 'Set booking notes',
    execute: async (args) => {
        const notes = String(args.notes ?? '').trim().slice(0, 2000);
        if (!notes) return 'set_booking_notes needs the note text.';
        const result = await askSurface('booking', 'setNotes', { notes }, 4000);
        return result ?? 'The booking form did not respond — is it open? Use start_booking first, then set the notes again.';
    },
  },

  set_booking_automations: {
    chip: (args) => (args.disabled === true || args.disabled === 'true' ? 'Removed booking from automations' : 'Re-enabled booking automations'),
    execute: async (args) => {
        const disabled = args.disabled === true || args.disabled === 'true';
        const result = await askSurface('booking', 'setAutomationsDisabled', { disabled }, 4000);
        return result ?? 'The booking form did not respond — is it open? Use start_booking first, then set this again.';
    },
  },

  manage_booking_resources: {
    chip: (args) => {
      const verb = String(args.action ?? '') === 'remove' ? 'Removed' : 'Assigned';
      return `${verb} ${String(args.name ?? 'a resource')}`.trim();
    },
    execute: async (args) => {
        const action = String(args.action || '').trim().toLowerCase();
        if (action !== 'add' && action !== 'remove') {
          return 'manage_booking_resources needs action "add" or "remove".';
        }
        const resourceName = String(args.name || '').trim();
        if (!resourceName) return 'manage_booking_resources needs the team member\'s name.';
        const result = await askSurface(
          'booking',
          'manageResource',
          { action, query: resourceName },
          8000,
        );
        return result ?? 'The booking form did not respond — is it open? Use start_booking first.';
    },
  },

  // ── Control Centre Tasks (todo_* — checklist to-dos; NOT calendar Events) ─

  list_todo_lists: {
    chip: () => 'Checked Task lists',
    execute: async (_args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot list Task lists right now.';
        const { listTodoListsForAssistant } = await import('./todoLookup');
        return listTodoListsForAssistant(ctx.businessId, {
          person: args.person !== undefined ? String(args.person) : undefined,
        });
    },
  },

  create_todo_list: {
    chip: (args) =>
      args.name ? `Created list "${String(args.name).slice(0, 40)}"` : 'Created a Task list',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot create Task lists right now.';
        const { createTodoListForAssistant } = await import('./todoLookup');
        return createTodoListForAssistant(ctx.businessId, {
          name: String(args.name || '').trim(),
          person: args.person !== undefined ? String(args.person) : undefined,
        });
    },
  },

  create_todo: {
    chip: (args) =>
      args.title ? `Created Task "${String(args.title).slice(0, 40)}"` : 'Created a Task',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot create Tasks right now.';
        const { createTodoForAssistant } = await import('./todoLookup');
        return createTodoForAssistant(ctx.businessId, {
          title: String(args.title || '').trim(),
          notes: String(args.notes || '').trim(),
          list: String(args.list || '').trim(),
          intent_hint: String(args.intent_hint || '').trim(),
          due_date: String(args.due_date || '').trim(),
          due_time: String(args.due_time || '').trim(),
          customer_id: String(args.customer_id || '').trim(),
          assignee: String(args.assignee || '').trim(),
          priority: String(args.priority || '').trim(),
          estimated_minutes:
            args.estimated_minutes === null || args.estimated_minutes === ''
              ? null
              : args.estimated_minutes !== undefined
                ? Number(args.estimated_minutes)
                : undefined,
          person: args.person !== undefined ? String(args.person) : undefined,
        });
    },
  },

  find_todos: {
    chip: (args) => {
      if (args.important === true || args.important === 'true') return 'Checked important Tasks';
      if (args.overdue === true || args.overdue === 'true') return 'Checked due & overdue Tasks';
      if (args.due_date) return `Checked Tasks due ${String(args.due_date)}`;
      return `Searched Tasks${args.query ? ` for "${String(args.query)}"` : ''}`;
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot look up Tasks right now.';
        const { findTodosForAssistant } = await import('./todoLookup');
        return findTodosForAssistant(ctx.businessId, {
          query: String(args.query || '').trim(),
          list: String(args.list || '').trim(),
          status: String(args.status || '').trim(),
          due_date: String(args.due_date || '').trim(),
          overdue: args.overdue === true || args.overdue === 'true',
          important: args.important === true || args.important === 'true',
          assignee: String(args.assignee || '').trim(),
          customer_id: String(args.customer_id || '').trim(),
          person: args.person !== undefined ? String(args.person) : undefined,
          limit: Number(args.limit) || undefined,
        });
    },
  },

  update_todo: {
    chip: (args) => {
      if (args.delete === true || args.delete === 'true') return 'Deleted a Task';
      if (String(args.status) === 'completed') return 'Completed a Task';
      if (String(args.status) === 'open') return 'Reopened a Task';
      return 'Updated a Task';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot update Tasks right now.';
        const { updateTodoForAssistant } = await import('./todoLookup');
        return updateTodoForAssistant(ctx.businessId, {
          todo_id: String(args.todo_id || '').trim(),
          status: String(args.status || '').trim(),
          title: args.title !== undefined ? String(args.title) : undefined,
          notes: args.notes !== undefined ? String(args.notes) : undefined,
          list: args.list !== undefined ? String(args.list) : undefined,
          due_date:
            args.due_date === null
              ? null
              : args.due_date !== undefined
                ? String(args.due_date)
                : undefined,
          due_time:
            args.due_time === null
              ? null
              : args.due_time !== undefined
                ? String(args.due_time)
                : undefined,
          assignee:
            args.assignee === null
              ? null
              : args.assignee !== undefined
                ? String(args.assignee)
                : undefined,
          customer_id:
            args.customer_id === null
              ? null
              : args.customer_id !== undefined
                ? String(args.customer_id)
                : undefined,
          priority: args.priority !== undefined ? String(args.priority) : undefined,
          estimated_minutes:
            args.estimated_minutes === null || args.estimated_minutes === ''
              ? null
              : args.estimated_minutes !== undefined
                ? Number(args.estimated_minutes)
                : undefined,
          delete: args.delete === true || args.delete === 'true',
          person: args.person !== undefined ? String(args.person) : undefined,
        });
    },
  },

  bulk_delete_todos: {
    chip: (args) =>
      args?.confirm === true || args?.confirm === 'true'
        ? 'Bulk deleted Tasks'
        : 'Checked Tasks to delete',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot delete Tasks right now.';
        const { bulkDeleteTodosForAssistant } = await import('./todoLookup');
        return bulkDeleteTodosForAssistant(ctx.businessId, {
          list: args.list !== undefined ? String(args.list) : undefined,
          status: args.status !== undefined ? String(args.status) : undefined,
          confirm: args.confirm === true || args.confirm === 'true',
          person: args.person !== undefined ? String(args.person) : undefined,
        });
    },
  },

  open_todos: {
    chip: () => 'Opened Tasks',
    execute: async () => {
        const { openTodosUi } = await import('./todoUi');
        openTodosUi();
        // Instant — don't block on DOM confirmation
        return 'Opened Tasks on screen now (Inbox + all lists).';
    },
  },

  show_todo_list: {
    chip: (args) =>
      args.list ? `Opened list "${String(args.list).slice(0, 40)}"` : 'Opened a Task list',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot show Task lists right now.';
        const list = String(args.list || '').trim();
        if (!list) {
          return 'error: show_todo_list needs a list name (e.g. "Crystal Bright", "Reminders", "Inbox"). For the full Tasks drawer use open_todos.';
        }
        const statusRaw = String(args.status || 'open').trim().toLowerCase();
        const status =
          statusRaw === 'completed' || statusRaw === 'all' ? statusRaw : 'open';

        // Open UI immediately — host resolves typos (brite→bright) and loads the list
        const { showTodoListUi } = await import('./todoUi');
        showTodoListUi({ listName: list, status });

        // Optional quick resolve for a better chip/message (uses short cache; never blocks UI)
        try {
          const { getTodoLists } = await import('../todos');
          const { resolveTodoListId, resolveTodoViewer } = await import('./todoLookup');
          const viewer = await resolveTodoViewer(ctx.businessId);
          const lists = await getTodoLists(ctx.businessId, { viewer });
          const resolved = resolveTodoListId(lists, { listName: list });
          if (resolved.matchedName) {
            return `Opened "${resolved.matchedName}" on screen — only that list's tasks.`;
          }
          if (resolved.reason.startsWith('ambiguous-list:')) {
            return `Opened the list picker — several close matches: ${resolved.reason.slice('ambiguous-list:'.length).split('|').join(', ')}. Ask which one if the wrong list is showing.`;
          }
        } catch {
          /* UI already opening */
        }
        return `Opening "${list}" on screen now.`;
    },
  },

  get_free_slots: {
    chip: (args) =>
      args.from
        ? `Checked free time ${String(args.from)}–${String(args.to || args.from)}`
        : 'Checked free time',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot check free slots right now.';
        const { getFreeSlotsForAssistant } = await import('./eventLookup');
        return getFreeSlotsForAssistant(ctx.businessId, {
          from: String(args.from || '').trim(),
          to: String(args.to || args.from || '').trim(),
          duration_minutes: Number(args.duration_minutes) || 60,
          assignee: String(args.assignee || '').trim(),
          allow_overlap: args.allow_overlap === true || args.allow_overlap === 'true',
          force_outside_hours:
            args.force_outside_hours === true || args.force_outside_hours === 'true',
          time_of_day: String(args.time_of_day || '').trim() || undefined,
          prefer_after: String(args.prefer_after || '').trim() || undefined,
          limit: Number(args.limit) || undefined,
        });
    },
  },

  create_calendar_event: {
    chip: (args) =>
      args.title
        ? `Scheduled "${String(args.title).slice(0, 36)}"`
        : args.todo_id
          ? 'Scheduled a Task on the calendar'
          : 'Created a calendar Event',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot create Events right now.';
        const { createCalendarEventForAssistant } = await import('./eventLookup');
        return createCalendarEventForAssistant(ctx.businessId, {
          title: String(args.title || '').trim(),
          notes: String(args.notes || '').trim(),
          date: String(args.date || '').trim(),
          time: String(args.time || '').trim(),
          end_date: String(args.end_date || '').trim(),
          end_time: String(args.end_time || '').trim(),
          duration_hours: Number(args.duration_hours) || undefined,
          duration_minutes: Number(args.duration_minutes) || undefined,
          todo_id: String(args.todo_id || '').trim(),
          customer_id: String(args.customer_id || '').trim(),
          assignee: String(args.assignee || '').trim(),
        });
    },
  },

  start_bulk_booking_followups: {
    chip: (args) =>
      Array.isArray(args?.customer_names) && args.customer_names.length > 0
        ? `Started follow-up Tasks for ${args.customer_names.length} customer${args.customer_names.length === 1 ? '' : 's'}`
        : 'Started bulk follow-up Tasks',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot start bulk follow-ups right now.';
        const { startBulkBookingFollowupsForAssistant } = await import('./bulkTodoJobs');
        return startBulkBookingFollowupsForAssistant(ctx.businessId, {
          booking_from: String(args.booking_from || '').trim(),
          booking_to: String(args.booking_to || '').trim(),
          customer_names: Array.isArray(args.customer_names)
            ? args.customer_names.map((n: unknown) => String(n)).filter(Boolean)
            : undefined,
          booking_status: String(args.booking_status || '').trim(),
          due_offset_months:
            args.due_offset_months !== undefined && args.due_offset_months !== ''
              ? Number(args.due_offset_months)
              : undefined,
          due_offset_days:
            args.due_offset_days !== undefined && args.due_offset_days !== ''
              ? Number(args.due_offset_days)
              : undefined,
          due_base: String(args.due_base || '').trim(),
          title_template: String(args.title_template || '').trim(),
          list: String(args.list || '').trim(),
          assignee: String(args.assignee || '').trim(),
          notes_template: String(args.notes_template || '').trim(),
        });
    },
  },

  start_task: {
    chip: () => 'Opened Create Event form',
    execute: async (args) => {
        if (!document.querySelector('[data-assistant-fab-option="Event"]')) {
          return '"Event" is not enabled in this user\'s create menu — its Calendar app setting is off. Turn it on with set_setting (app=calendar, setting="Events", state=on) or ask the user first.';
        }
        let customer: unknown = null;
        const customerId = String(args.customer_id || '').trim();
        if (customerId) {
          const { getCachedCustomer, clearGhostCustomers } = await import('./customerLookup');
          customer = getCachedCustomer(customerId);
          if (!customer) {
            try {
              const { getCustomer } = await import('../customers');
              customer = await getCustomer(customerId);
            } catch {
              customer = null;
            }
          }
          clearGhostCustomers();
          if (!customer) {
            return `customer_id "${customerId}" was not found — run lookup_customer first and use the id it returns.`;
          }
        }
        window.dispatchEvent(new CustomEvent('assistant:createTask', { detail: { customer } }));
        const el = await waitForVisibleElementByText('Create Event', 5000);
        if (!el) {
          return 'Tried to open the Create Event form but could not confirm it appeared — use look_at_screen to check what\'s on screen.';
        }
        const name = customer && typeof customer === 'object'
          ? `${(customer as { first_name?: string }).first_name || ''} ${(customer as { last_name?: string }).last_name || ''}`.trim()
          : '';
        const customerNote = name
          ? ` with ${name} attached as the customer`
          : ' (no customer attached — that\'s fine, events don\'t need one)';
        return `The Create Event form is now open on the user's screen${customerNote}, prefilled with today 9:00-10:00. Set the title with set_task_details, then the schedule if the user named a day/time.`;
    },
  },

  set_task_details: {
    chip: (args) => (args.title ? `Set event title: "${String(args.title)}"` : 'Set event details'),
    execute: async (args) => {
        const title = String(args.title ?? '').trim().slice(0, 200);
        const notes = String(args.notes ?? '').trim().slice(0, 2000);
        const address = String(args.address ?? '').trim().slice(0, 300);
        if (!title && !notes && !address) return 'set_task_details needs a title, notes, or address to set.';
        const result = await askSurface('task', 'setDetails', { title, notes, address }, 4000);
        return result ?? 'The event form did not respond — is it open? Use start_task first, then set the details again.';
    },
  },

  set_task_schedule: {
    chip: (args) => {
      if (args.date || args.time) return `Scheduled event for ${String(args.date ?? '')} ${String(args.time ?? '')}`.trim();
      if (args.duration_hours) return `Set event length to ${Number(args.duration_hours)}h`;
      return 'Adjusted event schedule';
    },
    execute: async (args) => {
        const date = String(args.date || '').trim();
        const time = String(args.time || '').trim();
        const endDate = String(args.end_date || '').trim();
        const endTime = String(args.end_time || '').trim();
        const durationHours = Number(args.duration_hours) || 0;
        const addHours = Number(args.add_hours) || 0;
        if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'date must be YYYY-MM-DD (resolve relative dates from the current date first).';
        if (time && !/^\d{2}:\d{2}$/.test(time)) return 'time must be HH:MM 24-hour. If the user gave no time, ask for one.';
        if (endDate && !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return 'end_date must be YYYY-MM-DD.';
        if (endTime && !/^\d{2}:\d{2}$/.test(endTime)) return 'end_time must be HH:MM 24-hour (3pm → 15:00).';
        if (durationHours < 0 || durationHours > 24 * 14) return 'duration_hours must be a positive number of hours.';
        if (!date && !time && !endDate && !endTime && !durationHours && !addHours) {
          return 'set_task_schedule needs something to change: date/time, end_date/end_time, duration_hours, or add_hours.';
        }
        const result = await askSurface('task', 'setSchedule', { date, time, endDate, endTime, durationHours, addHours }, 4000);
        return result ?? 'The event form did not respond — is it open? Use start_task first, then set the schedule again.';
    },
  },

  manage_task_resources: {
    chip: (args) => {
      const verb = String(args.action ?? '') === 'remove' ? 'Removed' : 'Assigned';
      return `${verb} ${String(args.name ?? 'a resource')}`.trim();
    },
    execute: async (args) => {
        const action = String(args.action || '').trim().toLowerCase();
        if (action !== 'add' && action !== 'remove') {
          return 'manage_task_resources needs action "add" or "remove".';
        }
        const resourceName = String(args.name || '').trim();
        if (!resourceName) return 'manage_task_resources needs the team member\'s name.';
        const result = await askSurface('task', 'manageResource', { action, query: resourceName }, 8000);
        return result ?? 'The event form did not respond — is it open? Use start_task first.';
    },
  },

  find_tasks: {
    chip: (args) => `Searched events${args.query ? ` for "${String(args.query)}"` : ''}`,
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot look up events right now.';
        const query = String(args.query || '').trim().slice(0, 120);
        const date = String(args.date || '').trim();
        const status = String(args.status || '').trim();
        if (!query && !date && !status) return 'find_tasks needs words from the title, a date, or a status to search by.';
        const { findTasks } = await import('./taskLookup');
        const tasks = await findTasks(ctx.businessId, { query, date, status });
        if (tasks.length === 0) {
          return `No events found${query ? ` matching "${query}"` : ''}${date ? ` on ${date}` : ''} — broaden the words once yourself; still nothing → tell the user.`;
        }
        const describe = (t: { id: string; title: string; status: string; start_date: string; start_time: string; assignees: string[] }) =>
          `"${t.title}" — ${t.start_date} ${t.start_time}, status: ${t.status}${t.assignees.length ? `, assigned: ${t.assignees.join(', ')}` : ''} — task_id: ${t.id}`;
        if (tasks.length === 1) {
          return `Found exactly one event: ${describe(tasks[0])}. Act on it with update_task — no need to ask.`;
        }
        return `${tasks.length} events match — do NOT pick one yourself. Ask the user which they mean, citing the dates/times, then update_task with the chosen task_id:\n${tasks.map(describe).join('\n')}`;
    },
  },

  update_task: {
    chip: (args) => {
      if (args.delete === true) return 'Deleted the event';
      if (String(args.status) === 'completed') return 'Marked event completed';
      if (args.date || args.time) return 'Rescheduled the event';
      return 'Updated the event';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot update tasks right now.';
        const taskId = String(args.task_id || '').trim();
        if (!taskId) return 'update_task needs the task_id from find_tasks.';
        const { updateTask } = await import('./taskLookup');
        return updateTask(ctx.businessId, taskId, {
          status: args.status ? String(args.status) : undefined,
          date: args.date ? String(args.date) : undefined,
          time: args.time ? String(args.time) : undefined,
          end_date: args.end_date ? String(args.end_date) : undefined,
          end_time: args.end_time ? String(args.end_time) : undefined,
          duration_hours: args.duration_hours ? Number(args.duration_hours) : undefined,
          title: args.title ? String(args.title) : undefined,
          notes: typeof args.notes === 'string' ? args.notes : undefined,
          delete: args.delete === true,
        });
    },
  },

  start_proposal: {
    chip: () => 'Opened New Proposal form',
    execute: async (args) => {
        if (!document.querySelector('[data-assistant-fab-option="Proposal"]')) {
          return '"Proposal" is not enabled in this user\'s create menu — its Proposals app setting is off. Turn it on with set_setting (app=proposals, setting="Proposal", state=on) or ask the user first.';
        }
        const customerId = String(args.customer_id || '').trim();
        if (!customerId) {
          return 'start_proposal needs a customer_id — proposals always have a customer. Run lookup_customer first; if the user never named one, ask who the proposal is for.';
        }
        const { getCachedCustomer, clearGhostCustomers } = await import('./customerLookup');
        let customer: unknown = getCachedCustomer(customerId);
        if (!customer) {
          try {
            const { getCustomer } = await import('../customers');
            customer = await getCustomer(customerId);
          } catch {
            customer = null;
          }
        }
        clearGhostCustomers();
        if (!customer) {
          return `customer_id "${customerId}" was not found — run lookup_customer first and use the id it returns.`;
        }
        window.dispatchEvent(new CustomEvent('assistant:createProposal', { detail: { customer } }));
        const el = await waitForVisibleElementByText('New Proposal', 5000);
        if (!el) {
          return 'Tried to open the New Proposal form but could not confirm it appeared — use look_at_screen to check what\'s on screen.';
        }
        const name = `${(customer as { first_name?: string }).first_name || ''} ${(customer as { last_name?: string }).last_name || ''}`.trim();
        return `The New Proposal form is now open on the user's screen with ${name || 'the customer'} selected. Set the title with set_proposal_details FIRST; expiry defaults to 30 days. At least one offer is still needed — build it with add_proposal_offer ONLY if the user has said what it should include (the proposal's name is not an offer); otherwise ask what the offer should include.`;
    },
  },

  add_proposal_offer: {
    chip: (args) => (args.title ? `Added offer "${String(args.title)}"` : 'Added an offer to the proposal'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot build offers right now.';
        const resolved = await resolveOfferServiceLines(ctx.businessId, args.services);
        if ('error' in resolved) return resolved.error;
        const title = String(args.title ?? '').trim().slice(0, 120);
        const deposit = parseDepositPercentage(args.deposit_percentage);
        if (typeof deposit === 'string') return deposit;
        const result = await askSurface(
          'proposal',
          'addOffer',
          {
            title,
            lines: resolved.lines,
            depositPercentage: deposit,
            saveCardOnDeposit: args.save_card_on_deposit !== false,
            vehicleQuery: String(args.vehicle || '').trim(),
            vehicleId: String(args.vehicle_id || '').trim(),
          },
          6000,
        );
        return result ?? 'The proposal form did not respond — is it open? Use start_proposal first, then add the offer again.';
    },
  },

  update_proposal_offer: {
    chip: (args) => {
      if (args.title) return `Renamed offer to "${String(args.title)}"`;
      if (args.deposit_percentage === null) return 'Removed the offer\'s deposit';
      if (args.deposit_percentage !== undefined) return `Set a ${Number(args.deposit_percentage)}% deposit`;
      if (args.remove_service) return `Removed ${String(args.remove_service)} from the offer`;
      if (args.add_services) return 'Added services to the offer';
      return 'Updated the offer';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot edit offers right now.';
        const hasDeposit = args.deposit_percentage !== undefined;
        const deposit = args.deposit_percentage === null ? null : parseDepositPercentage(args.deposit_percentage);
        if (typeof deposit === 'string') return deposit;
        let addLines: OfferServiceLine[] | null = null;
        if (args.add_services !== undefined) {
          const resolved = await resolveOfferServiceLines(ctx.businessId, args.add_services);
          if ('error' in resolved) return resolved.error;
          addLines = resolved.lines;
        }
        const title = String(args.title ?? '').trim().slice(0, 120);
        const removeService = String(args.remove_service ?? '').trim();
        if (!title && !hasDeposit && args.save_card_on_deposit === undefined && !addLines && !removeService) {
          return 'update_proposal_offer needs something to change: title, deposit_percentage, save_card_on_deposit, add_services, or remove_service.';
        }
        const result = await askSurface(
          'proposal',
          'updateOffer',
          {
            offerQuery: String(args.offer ?? '').trim(),
            title,
            depositPercentage: hasDeposit ? deposit : undefined,
            saveCardOnDeposit: typeof args.save_card_on_deposit === 'boolean' ? args.save_card_on_deposit : undefined,
            addLines,
            removeService,
          },
          6000,
        );
        return result ?? 'The proposal form did not respond — is it open? Use start_proposal first.';
    },
  },

  update_offer_service: {
    chip: (_args, result) => {
      const updated = result.match(/^Updated "([^"]+)"/);
      return updated ? `Updated "${updated[1]}" on the proposal` : 'Updated an offer service';
    },
    execute: async (args) => {
        const newTitle = String(args.new_title ?? '').trim().slice(0, 200);
        const newDescription = String(args.new_description ?? '').trim().slice(0, 2000);
        const newPrice = args.new_price !== undefined && args.new_price !== null && args.new_price !== '' ? Number(args.new_price) : null;
        if (newPrice !== null && (!Number.isFinite(newPrice) || newPrice < 0)) {
          return 'new_price must be a number of dollars, e.g. 250.';
        }
        const newDurationHours = args.new_duration_hours !== undefined && args.new_duration_hours !== null && args.new_duration_hours !== '' ? Number(args.new_duration_hours) : null;
        if (newDurationHours !== null && (!Number.isFinite(newDurationHours) || newDurationHours <= 0)) {
          return 'new_duration_hours must be a positive number of hours, e.g. 2.5.';
        }
        if (!newTitle && !newDescription && newPrice === null && newDurationHours === null) {
          return 'update_offer_service needs something to change: new_price, new_title, new_description, or new_duration_hours.';
        }
        const result = await askSurface(
          'proposal',
          'updateOfferService',
          {
            offerQuery: String(args.offer ?? '').trim(),
            serviceQuery: String(args.service ?? '').trim(),
            newTitle,
            newDescription,
            newPrice,
            newDurationMinutes: newDurationHours !== null ? Math.round(newDurationHours * 60) : null,
          },
          6000,
        );
        return result ?? 'The proposal form did not respond — is it open? Use start_proposal first.';
    },
  },

  set_proposal_details: {
    chip: (args) => (args.title ? `Set proposal title: "${String(args.title)}"` : 'Set proposal details'),
    execute: async (args) => {
        const title = String(args.title ?? '').trim().slice(0, 200);
        const expiryDays = Number(args.expiry_days) || 0;
        const expiryDate = String(args.expiry_date ?? '').trim();
        if (!title && !expiryDays && !expiryDate) return 'set_proposal_details needs a title and/or an expiry to set.';
        if (expiryDays && ![15, 30, 60].includes(expiryDays)) {
          return 'expiry_days must be 15, 30, or 60 (the form\'s presets) — for any other day, pass expiry_date as YYYY-MM-DD instead.';
        }
        if (expiryDate && !/^\d{4}-\d{2}-\d{2}$/.test(expiryDate)) {
          return 'expiry_date must be YYYY-MM-DD (resolve relative dates from the current date first).';
        }
        const result = await askSurface('proposal', 'setDetails', { title, expiryDays, expiryDate }, 4000);
        return result ?? 'The proposal form did not respond — is it open? Use start_proposal first, then set the details again.';
    },
  },

  // ── Existing proposals: recap, read, edit, send, convert, open ──────────

  find_proposals: {
    chip: (args) => (args.activity ? `Checked proposals ${String(args.activity)}` : 'Checked proposals'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot look up proposals right now.';
        const { findProposalsForAssistant } = await import('./proposalsLookup');
        return findProposalsForAssistant(ctx.businessId, {
          query: String(args.query || '').trim(),
          status: String(args.status || '').trim(),
          activity: String(args.activity || '').trim(),
          since: String(args.since || '').trim(),
          limit: Number(args.limit) || undefined,
        });
    },
  },

  read_proposal: {
    chip: () => 'Read a proposal',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read proposals right now.';
        const { readProposalForAssistant } = await import('./proposalsLookup');
        return readProposalForAssistant(ctx.businessId, {
          proposal: String(args.proposal || '').trim(),
          query: String(args.query || '').trim(),
        });
    },
  },

  update_proposal: {
    chip: (args) => (args.title ? `Renamed a proposal to "${String(args.title)}"` : 'Updated a proposal'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot update proposals right now.';
        const { updateProposalForAssistant } = await import('./proposalsLookup');
        return updateProposalForAssistant(ctx.businessId, {
          proposal: String(args.proposal || '').trim(),
          query: String(args.query || '').trim(),
          title: String(args.title || '').trim(),
          expiry_date: String(args.expiry_date || '').trim(),
        });
    },
  },

  send_proposal: {
    chip: (args) => `Sent a proposal link${args.channel === 'email' ? ' by email' : args.channel === 'sms' ? ' by text' : ''}`,
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot send proposals right now.';
        const { sendProposalForAssistant } = await import('./proposalsLookup');
        return sendProposalForAssistant(ctx.businessId, {
          proposal: String(args.proposal || '').trim(),
          query: String(args.query || '').trim(),
          channel: String(args.channel || '').trim(),
        });
    },
  },

  convert_proposal_to_booking: {
    chip: () => 'Opened the booking form from a proposal',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot convert proposals right now.';
        const { resolveProposalForConversion } = await import('./proposalsLookup');
        const { full, who, error } = await resolveProposalForConversion(ctx.businessId, {
          proposal: String(args.proposal || '').trim(),
          query: String(args.query || '').trim(),
        });
        if (!full) return error || 'error: could not find that proposal.';
        window.dispatchEvent(new CustomEvent('assistant:convertProposal', { detail: { proposal: full } }));
        const el = await waitForVisibleElementByText('Create Booking', 5000);
        if (!el) {
          return 'Tried to open the booking form from the proposal but could not confirm it appeared — use look_at_screen to check.';
        }
        const approved = (full.offers || []).find((o) => o.status === 'approved');
        const offerNote = approved
          ? `the approved offer "${approved.title}"`
          : 'the first offer (none was approved yet)';
        return (
          `The Create Booking form is open, prefilled from ${who}'s proposal "${full.title}": customer, services, prices, and deposits from ${offerNote} carried over, and any payments transfer when it's created. ` +
          'From here it is a NORMAL booking flow: set_booking_schedule with the date/time the user gave (ask if they have not said when), a resource at wrap-up, then READY → the user must confirm before you press Create Booking. Creating it marks the proposal booked automatically.'
        );
    },
  },

  open_proposal: {
    chip: () => 'Opened a proposal on screen',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot open proposals right now.';
        const { resolveProposalLink } = await import('./proposalsLookup');
        const target = await resolveProposalLink(ctx.businessId, {
          proposal: String(args.proposal || '').trim(),
          query: String(args.query || '').trim(),
        });
        if (!target.path) return target.error || 'error: could not find that proposal.';
        deepLinkNavigate(target.path);
        return `Opened ${target.who}'s proposal "${target.title}" on screen — the user is looking at its details now.`;
    },
  },

  // ── CRM leads: recap, read, edit/move, create ───────────────────────────

  find_leads: {
    chip: (args) => (args.new_only ? 'Checked new leads' : 'Checked CRM leads'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot look up leads right now.';
        const { findLeadsForAssistant } = await import('./crmLookup');
        return findLeadsForAssistant(ctx.businessId, {
          query: String(args.query || '').trim(),
          pipeline: String(args.pipeline || '').trim(),
          stage: String(args.stage || '').trim(),
          status: String(args.status || '').trim(),
          new_only: args.new_only === true || args.new_only === 'true',
          since: String(args.since || '').trim(),
          limit: Number(args.limit) || undefined,
        });
    },
  },

  read_lead: {
    chip: () => 'Read a lead',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read leads right now.';
        const { readLeadForAssistant } = await import('./crmLookup');
        return readLeadForAssistant(ctx.businessId, {
          lead: String(args.lead || '').trim(),
          query: String(args.query || '').trim(),
        });
    },
  },

  open_lead: {
    chip: () => 'Opened a lead on screen',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot open leads right now.';
        const { resolveLeadLink } = await import('./crmLookup');
        const target = await resolveLeadLink(ctx.businessId, {
          lead: String(args.lead || '').trim(),
          query: String(args.query || '').trim(),
        });
        if (!target.id) return target.error || 'error: could not find that lead.';
        const idText = String(target.id);
        try { sessionStorage.setItem('assistant_open_opportunity', idText); } catch { /* storage unavailable */ }
        deepLinkNavigate('/crm');
        window.dispatchEvent(new CustomEvent('assistant:openOpportunity', { detail: { opportunityId: target.id } }));
        return `Opened ${target.who}'s ${target.label} on screen — the user is looking at the opportunity details now.`;
    },
  },

  update_lead: {
    chip: (args) => {
      if (args.stage) return `Moved a lead to "${String(args.stage)}"`;
      if (args.pipeline) return `Moved a lead to the ${String(args.pipeline)} pipeline`;
      if (args.status) return `Marked a lead ${String(args.status)}`;
      if (args.follow_up_at) return 'Scheduled a lead follow-up';
      return 'Updated a lead';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot update leads right now.';
        const { updateLeadForAssistant } = await import('./crmLookup');
        return updateLeadForAssistant(ctx.businessId, {
          lead: String(args.lead || '').trim(),
          query: String(args.query || '').trim(),
          stage: String(args.stage || '').trim(),
          pipeline: String(args.pipeline || '').trim(),
          status: String(args.status || '').trim(),
          lost_reason: String(args.lost_reason || '').trim(),
          title: String(args.title || '').trim(),
          value: args.value as number | string,
          source: String(args.source || '').trim(),
          notes: String(args.notes || '').trim(),
          follow_up_at: String(args.follow_up_at || '').trim(),
          clear_follow_up: args.clear_follow_up === true || args.clear_follow_up === 'true',
          complete_follow_up: args.complete_follow_up === true || args.complete_follow_up === 'true',
        });
    },
  },

  create_lead: {
    chip: () => 'Created a CRM lead',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot create leads right now.';
        const customerId = String(args.customer_id || '').trim();
        if (!customerId) return 'error: create_lead needs a customer_id — run lookup_customer first and use the id it returns (or create_customer for someone brand new).';
        const { getCachedCustomer, clearGhostCustomers } = await import('./customerLookup');
        let customer = getCachedCustomer(customerId) as { id: string; first_name?: string; last_name?: string } | null;
        if (!customer) {
          try {
            const { getCustomer } = await import('../customers');
            customer = await getCustomer(customerId);
          } catch {
            customer = null;
          }
        }
        clearGhostCustomers();
        if (!customer) return `error: customer_id "${customerId}" was not found — run lookup_customer first and use the id it returns.`;
        const { createLeadForAssistant } = await import('./crmLookup');
        return createLeadForAssistant(ctx.businessId, {
          customer,
          pipeline: String(args.pipeline || '').trim(),
          stage: String(args.stage || '').trim(),
          title: String(args.title || '').trim(),
          value: args.value as number | string,
          source: String(args.source || '').trim(),
          notes: String(args.notes || '').trim(),
        });
    },
  },

  // ── Calendar: existing bookings — find, read, edit, open ────────────────

  find_bookings: {
    chip: (args) => (args.date ? `Checked bookings for ${String(args.date)}` : 'Checked the calendar'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot look up bookings right now.';
        const { findBookingsForAssistant } = await import('./calendarLookup');
        return findBookingsForAssistant(ctx.businessId, {
          query: String(args.query || '').trim(),
          date: String(args.date || '').trim(),
          from: String(args.from || '').trim(),
          to: String(args.to || '').trim(),
          status: String(args.status || '').trim(),
          type: String(args.type || '').trim(),
          unscheduled: args.unscheduled === true || args.unscheduled === 'true',
          limit: Number(args.limit) || undefined,
        });
    },
  },

  read_booking: {
    chip: (args) => {
      const d = String(args.detail || '').toLowerCase();
      if (/rate|flat/.test(d)) return 'Read a booking\u2019s RateBook';
      if (/profit|loss|margin/.test(d)) return 'Read a booking\u2019s profit & loss';
      if (/expense|material|cost/.test(d)) return 'Read a booking\u2019s expenses';
      return 'Read a booking';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read bookings right now.';
        const { readBookingForAssistant } = await import('./calendarLookup');
        return readBookingForAssistant(ctx.businessId, {
          booking: String(args.booking || '').trim(),
          query: String(args.query || '').trim(),
          detail: String(args.detail || args.tab || '').trim(),
        });
    },
  },

  update_booking: {
    chip: (args) => {
      if (args.status) return `Marked a booking ${String(args.status)}`;
      if (args.date || args.time) return 'Rescheduled a booking';
      if (args.duration_minutes !== undefined || args.add_minutes !== undefined) return 'Changed a booking\u2019s length';
      if (args.assign_resource) return `Assigned ${String(args.assign_resource)}`;
      if (args.unassign_resource) return `Unassigned ${String(args.unassign_resource)}`;
      if (args.unschedule) return 'Unscheduled a booking';
      return 'Updated a booking';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot update bookings right now.';
        const { updateBookingForAssistant } = await import('./calendarLookup');
        return updateBookingForAssistant(ctx.businessId, {
          booking: String(args.booking || '').trim(),
          query: String(args.query || '').trim(),
          date: String(args.date || '').trim(),
          time: String(args.time || '').trim(),
          end_date: String(args.end_date || '').trim(),
          end_time: String(args.end_time || '').trim(),
          duration_minutes: args.duration_minutes as number | string,
          add_minutes: args.add_minutes as number | string,
          status: String(args.status || '').trim(),
          notes: String(args.notes || '').trim(),
          address: String(args.address || '').trim(),
          title: String(args.title || '').trim(),
          assign_resource: String(args.assign_resource || '').trim(),
          unassign_resource: String(args.unassign_resource || '').trim(),
          unschedule: args.unschedule === true || args.unschedule === 'true',
          disable_automations: args.disable_automations === undefined
            ? undefined
            : args.disable_automations === true || args.disable_automations === 'true',
        });
    },
  },

  open_booking: {
    chip: () => 'Opened a booking on screen',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot open bookings right now.';
        const { resolveBookingLink } = await import('./calendarLookup');
        const target = await resolveBookingLink(ctx.businessId, {
          booking: String(args.booking || '').trim(),
          query: String(args.query || '').trim(),
        });
        if (!target.id) return target.error || 'error: could not find that booking.';
        try { sessionStorage.setItem('assistant_open_booking', target.id); } catch { /* storage unavailable */ }
        deepLinkNavigate('/calendarV2');
        window.dispatchEvent(new CustomEvent('assistant:openBooking', { detail: { bookingId: target.id } }));
        return `Opened ${target.who}'s booking ${target.label} on screen — the user is looking at its details now.`;
    },
  },

  // ── Invoices — find, read, open, outstanding, Stripe, statements, payments ─

  find_invoices: {
    chip: (args) => (args.query ? `Looked up invoices for ${String(args.query)}` : 'Looked up invoices'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot look up invoices right now.';
        const { findInvoicesForAssistant } = await import('./invoicesLookup');
        return findInvoicesForAssistant(ctx.businessId, {
          query: String(args.query || '').trim(),
          status: String(args.status || '').trim(),
          from: String(args.from || '').trim(),
          to: String(args.to || '').trim(),
          limit: Number(args.limit) || undefined,
        });
    },
  },

  read_invoice: {
    chip: () => 'Read an invoice',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read invoices right now.';
        const { readInvoiceForAssistant } = await import('./invoicesLookup');
        return readInvoiceForAssistant(ctx.businessId, {
          invoice: String(args.invoice || '').trim(),
          query: String(args.query || '').trim(),
        });
    },
  },

  open_invoice: {
    chip: () => 'Opened an invoice on screen',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot open invoices right now.';
        const { resolveInvoiceLink } = await import('./invoicesLookup');
        const target = await resolveInvoiceLink(ctx.businessId, {
          invoice: String(args.invoice || '').trim(),
          query: String(args.query || '').trim(),
        });
        if (!target.path) return target.error || 'error: could not find that invoice.';
        deepLinkNavigate(target.path);
        window.dispatchEvent(new CustomEvent('assistant:openInvoice', { detail: { path: target.path } }));
        return `Opened the invoice for ${target.who} on screen — the user is looking at it now.`;
    },
  },

  get_outstanding: {
    chip: (args) => (args.query ? `Checked what ${String(args.query)} owes` : 'Checked outstanding payments'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot check outstanding payments right now.';
        const { getOutstandingForAssistant } = await import('./invoicesLookup');
        return getOutstandingForAssistant(ctx.businessId, { query: String(args.query || '').trim() });
    },
  },

  get_stripe_money: {
    chip: () => 'Checked Stripe payouts',
    execute: async (_args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot check Stripe right now.';
        const { getStripeMoneyForAssistant } = await import('./invoicesLookup');
        return getStripeMoneyForAssistant(ctx.businessId);
    },
  },

  send_statement: {
    chip: (args) => (args.all ? 'Sent statements' : 'Sent a statement'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot send statements right now.';
        const { sendStatementForAssistant } = await import('./invoicesLookup');
        return sendStatementForAssistant(ctx.businessId, {
          customer: String(args.customer || '').trim(),
          all: args.all === true || args.all === 'true',
          confirm: args.confirm === true || args.confirm === 'true',
          message: String(args.message || '').trim() || undefined,
        });
    },
  },

  record_invoice_payment: {
    chip: () => 'Recorded a payment',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot record payments right now.';
        const { recordInvoicePaymentForAssistant } = await import('./invoicesLookup');
        return recordInvoicePaymentForAssistant(ctx.businessId, {
          invoice: String(args.invoice || '').trim(),
          query: String(args.query || '').trim(),
          amount: args.amount as number | string | undefined,
          method: String(args.method || '').trim(),
        });
    },
  },

  // ── Actions Required — the Control Centre inbox + resolving its items ─────
  get_actions_required: {
    chip: () => 'Checked Actions Required',
    execute: async (_args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot check Actions Required right now.';
        const { getActionsRequiredForAssistant } = await import('./actionsLookup');
        return getActionsRequiredForAssistant(ctx.businessId);
    },
  },
  read_payroll_request: {
    chip: () => 'Read a payroll request',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read payroll requests right now.';
        const { readPayrollRequestForAssistant } = await import('./actionsLookup');
        return readPayrollRequestForAssistant(ctx.businessId, String(args.action || args.request || '').trim());
    },
  },
  resolve_action: {
    chip: (args) => {
      const verb = String(args.verb || '').toLowerCase();
      if (verb.startsWith('approve')) return 'Approved a request';
      if (verb.startsWith('deny') || verb.startsWith('decline') || verb.startsWith('reject')) return 'Denied a request';
      return 'Resolved an action item';
    },
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot resolve actions right now.';
        const { resolveActionForAssistant } = await import('./actionsLookup');
        return resolveActionForAssistant(ctx.businessId, {
          action: String(args.action || '').trim(),
          verb: String(args.verb || '').trim(),
          note: String(args.note || '').trim(),
        });
    },
  },

  // ── Team members — find, read, create, update (wages, roles, hours, seats) ─
  find_team_members: {
    chip: (args) => (args.query ? `Looked up team member ${String(args.query)}` : 'Looked at the team'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot look up the team right now.';
        const { findTeamMembersForAssistant } = await import('./teamLookup');
        return findTeamMembersForAssistant(ctx.businessId, {
          query: String(args.query || '').trim(),
          status: String(args.status || '').trim(),
        });
    },
  },
  read_team_member: {
    chip: () => 'Read a team member profile',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read team members right now.';
        const { readTeamMemberForAssistant } = await import('./teamLookup');
        return readTeamMemberForAssistant(ctx.businessId, {
          member: String(args.member || '').trim(),
          query: String(args.query || '').trim(),
        });
    },
  },
  locate_team_member: {
    chip: (args) => (args.who ? `Located ${String(args.who)}` : 'Located the team'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot locate team members right now.';
        const { locateTeamMemberForAssistant } = await import('./teamLookup');
        return locateTeamMemberForAssistant(ctx.businessId, { who: String(args.who || '').trim() });
    },
  },
  create_team_member: {
    chip: (args) => `Created team member ${String(args.first_name || '')} ${String(args.last_name || '')}`.trim(),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot create team members right now.';
        const { createTeamMemberForAssistant } = await import('./teamLookup');
        return createTeamMemberForAssistant(ctx.businessId, {
          first_name: String(args.first_name || '').trim(),
          last_name: String(args.last_name || '').trim(),
          email: String(args.email || '').trim(),
          phone: String(args.phone || '').trim(),
          role: String(args.role || '').trim(),
          admin: args.admin === true || args.admin === 'true',
          invite: args.invite === true || args.invite === 'true',
          compensation_type: String(args.compensation_type || '').trim(),
          rate: args.rate as number | string | undefined,
        });
    },
  },
  update_team_member: {
    chip: () => 'Updated a team member',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot update team members right now.';
        const { updateTeamMemberForAssistant } = await import('./teamLookup');
        return updateTeamMemberForAssistant(ctx.businessId, {
          member: String(args.member || '').trim(),
          query: String(args.query || '').trim(),
          first_name: String(args.first_name || '').trim(),
          last_name: String(args.last_name || '').trim(),
          email: String(args.email || '').trim(),
          phone: String(args.phone || '').trim(),
          role: String(args.role || '').trim(),
          admin: args.admin as boolean | string | undefined,
          bookable: args.bookable as boolean | string | undefined,
          active: args.active as boolean | string | undefined,
          send_invite: args.send_invite === true || args.send_invite === 'true',
          compensation_type: String(args.compensation_type || '').trim(),
          rate: args.rate as number | string | undefined,
          hours_days: String(args.hours_days || '').trim(),
          hours_start: String(args.hours_start || '').trim(),
          hours_end: String(args.hours_end || '').trim(),
          days_off: String(args.days_off || '').trim(),
        });
    },
  },

  get_conversations: {
    chip: (args) => (args.filter === 'unread' ? 'Checked unread messages' : 'Checked the inbox'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read messages right now.';
        const { getConversationsForAssistant } = await import('./messagesLookup');
        return getConversationsForAssistant(ctx.businessId, {
          filter: args.filter === 'unread' ? 'unread' : 'all',
          query: String(args.query || '').trim(),
          limit: Number(args.limit) || undefined,
        });
    },
  },

  send_message: {
    chip: (args) => `Sent a ${args.channel === 'email' ? 'reply email' : 'reply'}`,
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot send messages right now.';
        const { sendMessageForAssistant } = await import('./messagesLookup');
        return sendMessageForAssistant(ctx.businessId, {
          conversation: String(args.conversation || '').trim(),
          query: String(args.query || '').trim(),
          channel: args.channel === 'email' ? 'email' : args.channel === 'sms' ? 'sms' : undefined,
          body: String(args.body || ''),
          subject: String(args.subject || '').trim(),
        });
    },
  },

  read_conversation: {
    chip: () => 'Read a conversation',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot read messages right now.';
        const { readConversationForAssistant } = await import('./messagesLookup');
        return readConversationForAssistant(ctx.businessId, {
          conversation: String(args.conversation || '').trim(),
          query: String(args.query || '').trim(),
          limit: Number(args.limit) || undefined,
        });
    },
  },

  mark_read: {
    chip: (args) => (args.all === true || args.all === 'true' ? 'Marked all messages read' : 'Marked a conversation read'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot mark messages right now.';
        const { markReadForAssistant, markAllReadForAssistant } = await import('./messagesLookup');
        if (args.all === true || args.all === 'true') return markAllReadForAssistant(ctx.businessId);
        return markReadForAssistant(ctx.businessId, {
          conversation: String(args.conversation || '').trim(),
          query: String(args.query || '').trim(),
        });
    },
  },

  mute_sender: {
    chip: (args) => (args.unmute === true || args.unmute === 'true' ? 'Unmuted an email sender' : 'Marked an email sender as junk'),
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot manage junk senders right now.';
        const { muteSenderForAssistant } = await import('./messagesLookup');
        return muteSenderForAssistant(ctx.businessId, {
          email: String(args.email || '').trim(),
          query: String(args.query || '').trim(),
          unmute: args.unmute === true || args.unmute === 'true',
        });
    },
  },

  open_conversation: {
    chip: () => 'Opened a conversation on screen',
    execute: async (args, ctx) => {
        if (!ctx.businessId) return 'No business selected — cannot open conversations right now.';
        const { resolveConversationLink } = await import('./messagesLookup');
        const target = await resolveConversationLink(ctx.businessId, {
          conversation: String(args.conversation || '').trim(),
          query: String(args.query || '').trim(),
        });
        if (!target.path) return target.error || 'error: could not find that conversation.';
        deepLinkNavigate(target.path);
        return `Opened the conversation with ${target.who} on the Messages screen — the user is looking at it now. Reading it does not mark it read; mark_read does that if asked.`;
    },
  },

  cancel_form: {
    chip: (args) => `Cancelled the ${String(args.form || 'open')} form`,
    execute: async (args) => {
        const open = getOpenAssistantSurfaces();
        if (open.length === 0) return 'No form is open — nothing to cancel.';
        let target = String(args.form || '').trim().toLowerCase();
        if (!target) {
          if (open.length === 1) target = open[0];
          else return `Several forms are open (${open.join(', ')}) — call again with form set to the one to cancel.`;
        }
        if (!open.includes(target)) {
          return `The ${target} form isn't open. Open forms: ${open.join(', ')}.`;
        }
        const result = await askSurface(target, 'cancel', {}, 4000);
        return result ?? `Could not cancel the ${target} form — it did not respond.`;
    },
  },

  get_current_screen: {
    execute: () => {
        const current = findCurrentDestination();
        const where = current
          ? `${current.label} (${window.location.pathname}${window.location.search})`
          : `${window.location.pathname}${window.location.search}`;
        return `The user is currently on: ${where}.`;
    },
  },
} satisfies Record<string, AssistantToolExecutor>;

/** Tool names the assistant can call — derived from the registry, shared by voice and text surfaces. */
export type AssistantActionName = keyof typeof TOOL_EXECUTORS;
export const ASSISTANT_ACTION_NAMES = Object.keys(TOOL_EXECUTORS) as AssistantActionName[];

export function isAssistantActionName(name: unknown): name is AssistantActionName {
  return typeof name === 'string' && name in TOOL_EXECUTORS;
}

/** Failure detection for action result strings (mirrors the executors' error phrasing). */
export const ASSISTANT_ACTION_FAILURE_REGEX =
  /^Unknown|^error:|not installed|not visible|failed|^Could not|^Nothing|^No input|did not open|did not respond|is not an on\/off|could not confirm|must be/i;

/**
 * Fire-and-forget telemetry: one row per executed tool call so success rates,
 * latency, and failure patterns are queryable per business. Never awaited by
 * the caller and never allowed to break the user-facing flow.
 */
function logToolCall(
  ctx: AssistantActionContext,
  toolName: string,
  args: Record<string, unknown>,
  result: string,
  durationMs: number,
): void {
  if (!ctx.businessId) return;
  try {
    void supabase
      .from('assistant_tool_calls')
      .insert({
        business_id: ctx.businessId,
        source: ctx.source ?? 'text',
        tool_name: toolName,
        args,
        ok: !ASSISTANT_ACTION_FAILURE_REGEX.test(result),
        result_snippet: result.slice(0, 200),
        duration_ms: Math.round(durationMs),
      })
      .then(() => undefined);
  } catch {
    // Telemetry must never affect the assistant flow.
  }
}

/** Execute an assistant action via the registry and return a result string for the model. */
export async function executeAssistantAction(
  name: string,
  args: Record<string, unknown>,
  ctx: AssistantActionContext = {},
): Promise<string> {
  const executor = isAssistantActionName(name) ? (TOOL_EXECUTORS[name] as AssistantToolExecutor) : null;
  if (!executor) return `Unknown action "${name}".`;
  const startedAt = performance.now();
  try {
    const result = await executor.execute(args, ctx);
    logToolCall(ctx, name, args, result, performance.now() - startedAt);
    return result;
  } catch (err) {
    console.warn('[assistant] action failed:', name, err);
    const result = 'The action failed unexpectedly. Apologize briefly and guide the user manually instead.';
    logToolCall(ctx, name, args, result, performance.now() - startedAt);
    return result;
  }
}

/** Human label for a confirmation chip + failure detection from the result string. */
export function describeAssistantAction(name: string, args: Record<string, unknown>, result: string): AssistantActionChip {
  const ok = !ASSISTANT_ACTION_FAILURE_REGEX.test(result);
  if (!ok) {
    return { label: result.split('.')[0], ok };
  }
  const chip = isAssistantActionName(name) ? (TOOL_EXECUTORS[name] as AssistantToolExecutor).chip : undefined;
  return { label: chip ? chip(args, result) : 'Done', ok };
}
